import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useCallback,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";

import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import * as converter from "@shared/utils/ValueConverterUtils";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ColDef,
  TextEditorModule,
  AllEnterpriseModule,
  NumberEditorModule,
  CheckboxEditorModule,
  RichSelectModule,
  CellFocusedEvent,
  RowSelectionOptions,
  RowNode,
  CellPosition,
  CustomEditorModule,
  CellKeyDownEvent,
  SuppressKeyboardEventParams,
  GetMainMenuItemsParams,
  MenuItemDef,
  DefaultMenuItem,
  Column,
  GetContextMenuItemsParams,
} from "ag-grid-enterprise";

import {
  SnGridProps,
  ColumnConfig,
  SnGridEtcProps,
  SnGridEvents,
  CellValueChangedEvent,
  RowData,
  ChangeColumnConfig,
  CustomColDef,
  FlatRowType,
  ChangeGroupColumnConfig,
  SnGridRef,
} from "./SnGridTypes";
import { SnColumnsCreate, getRowTpColumn } from "./SnColumnsCreate";
import SnTab from "../SnTab";
import SnTabHeader from "../SnTabHeader";
import { v4 as uuidv4 } from "uuid";
import {
  global,
  useMenuStore,
  globalMenu,
  onSearchStore,
  menuChangeStore,
} from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { CustomHeaderCheckbox } from "./SnGridEditors";
import { isDateValue, isNumeric, CalAm } from "@shared/utils/CommonUtils";
import { createChatScreenDataFromGrid } from "@shared/utils/ChatScreenDataUtils";
import type { ChatSearchFormData } from "@shared/type/CommonType";
import {
  SnGridLocaleText,
  SnGridExcelStyles,
  NO_MATCH_QUICK_FILTER,
} from "./SnGridConstants";
import { PopupHelper } from "./SnGridPopup";
import { setPopupValue } from "./SnGridPopupFlag";
import { createMainMenuItems, FN_Fi_AutoSlip } from "./SnMenuItems";
import "./SnGrid.css";
import SnGridFindBar, { useSnGridFind } from "./SnGridFindBar";
import { useSnGridIme } from "./SnGridIme";
import { StyleSettings } from "../../../styles/StyleSettings";
import { showPopupModal, showResizablePopupModal } from "@widgets/CommonModal";
import SnGridDataInspector, { formatFilterModel } from "./SnGridDataInspector"; // 26.11.04 kdy 탭 제목 Ctrl+Shift+더블클릭 데이터 인스펙터
import HeaderPopup from "./HeaderPopup";

// 필요한 모듈 등록

const localeText = SnGridLocaleText;
const defaultCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});
const SnGrid = forwardRef<SnGridRef, SnGridProps>((props, ref) => {
  const {
    enablePasteOnlyAdd = false, // 26.03.16 kdy 클립보드 붙여넣기 (기존행은 데이터는 유지하면서 행추가 허용)
    columnConfig = [], // 컬럼 정의
    rowData = [], // 행 데이터
    etcProps,
    events,
    header,
    filter,
    headerInfo,
    popupParent,
  } = props;

  //#region 변수모음
  const gridRef = useRef<AgGridReact>(null); // AG Grid 내부 참조
  const gridDomRef = useRef<HTMLDivElement>(null); // 26.09.23 kdy 다중그리드 포커스 소유 판별용 래퍼 DOM
  const gridApiRef = useRef<any>(null); // AG Grid API
  const initialSortColIdRef = useRef<string | null>(null); // 26.10.30 화살표 숨긴 기본 정렬 컬럼 (사용자 정렬 전까지)
  // 26.10.21 셀을 선택만 한 상태에서 한글을 치면 첫 글자가 영문으로 들어가는 문제 (SnGridIme.tsx)
  const ime = useSnGridIme(
    gridApiRef,
    gridDomRef,
    etcProps?.isEditable ?? true
  );
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const usedKeys = new Set<string>(); // 이미 사용된 SN_SAL_SAO_REG_001_POP_001키를 추적
  const { userInfo } = global(); // 전역 사용자 정보
  const modifiedRowsRef = useRef({
    added: [] as RowData[],
    updated: [] as RowData[],
    deleted: [] as RowData[],
  });
  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    if (etcProps?.rowSelection === "single") {
      return {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else if (etcProps?.rowSelection === "multiple") {
      return {
        mode: "multiRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else {
      return {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      }; // 기본값
    }
  }, [etcProps?.rowSelection]);
  const isInitialFocusPending = useRef(false);
  // 26.08.21 kdy 조회 시 modelUpdated가 여러 번 발생해 0행 스크롤 보정이 경합하던 문제.
  // 보정을 예약해두고 마지막 modelUpdated 기준으로만 실행하기 위한 rAF 핸들.
  const initialFocusRaf = useRef<number | null>(null);
  const isDeleteFocusPending = useRef({
    pending: false, // 삭제 후 포커스 대기 여부
    index: 0, // 포커스할 대상 인덱스
  });
  const [isMemoizedRowDataChanged, setIsMemoizedRowDataChanged] =
    useState<any>(false);

  const [keyRowData, setKeyRowData] = useState<RowData[]>([]);
  const [recoveryData, setRecoveryData] = useState<any>({});
  const [initPinnedBottomRowData, setInitPinnedBottomRowData] = useState<any>(
    []
  );
  const popupCons = useRef<any>({});

  const popupContainer = useMemo(() => {
    // ID가 'ag-grid-popup-root'인 요소를 찾아 반환
    return document.querySelector("#ag-grid-popup-root");
  }, []);
  const isPopupOpen = useRef(false);
  // 코드 칸에 친 값을 Enter/Tab 으로 확정한 셀. 조회가 끝난 뒤 이동하거나 도움창을 띄운다 (handleCodeTyped)
  const pendingCodeTypedRef = useRef<{ node: any; colId: string } | null>(null);
  const [seq, setSeq] = useState<number>(1);
  const filterBoxRef = useRef<{ openFilterBox: () => void } | null>(null);
  const memoizedColumnConfig = useMemo(() => columnConfig, [columnConfig]);
  const chatMenuCode = memoizedColumnConfig[0]?.cd_menu || "";
  const chatGridKey = memoizedColumnConfig[0]?.cd_grid || "";
  const chatFiltersRef = useRef<ChatSearchFormData>({});
  const memoizedRowData = useMemo(() => rowData, [rowData]);
  const mainMenuItems = (params: GetMainMenuItemsParams) => {
    return createMainMenuItems(params, {
      columnSave,
      columnInit,
      headerChange,
      filterBoxRef,
      enableRowDrag,
      props,
      extraHeaderMenuItems: etcProps?.extraHeaderMenuItems,
      onExtraHeaderMenuClick: events?.onExtraHeaderMenuClick,
    });
  };
  const [focusedRowNode, setFocusedRowNode] = useState<RowNode>();
  const focusedRowNodeRef = useRef<RowNode>();
  const [isAddContext, setIsAddContext] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<
    Array<Array<{ name: string; group: string; action: () => void }>>
  >([]);

  // "컬럼 준비 완료" 상태 확인용 ref
  const columnsReadyResolveRef = useRef<(() => void) | null>(null);
  const columnsReadyPromiseRef = useRef<Promise<void> | null>(null);
  if (!columnsReadyPromiseRef.current) {
    columnsReadyPromiseRef.current = new Promise<void>((resolve) => {
      columnsReadyResolveRef.current = resolve;
    });
  }

  useEffect(() => {
    focusedRowNodeRef.current = focusedRowNode;
  }, [focusedRowNode]);

  //#region sort helpers (exclude insert rows from sorting)
  const defaultCompare = (a: any, b: any): number => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;

    if (isNumeric(a) && isNumeric(b)) {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      if (isNaN(na) && isNaN(nb)) return 0;
      if (isNaN(na)) return -1;
      if (isNaN(nb)) return 1;
      return na - nb;
    }

    if (isDateValue(a) && isDateValue(b)) {
      const ta = new Date(a).getTime();
      const tb = new Date(b).getTime();
      if (isNaN(ta) && isNaN(tb)) return 0;
      if (isNaN(ta)) return -1;
      if (isNaN(tb)) return 1;
      return ta - tb;
    }

    return defaultCollator.compare(String(a), String(b));
  };

  const wrapComparator = (colDef: any): any => {
    const original = colDef.comparator;
    return {
      ...colDef,
      comparator: (
        valueA: any,
        valueB: any,
        nodeA: RowNode,
        nodeB: RowNode,
        isInverted: boolean
      ) => {
        const aInsert = nodeA?.data?.state === "insert";
        const bInsert = nodeB?.data?.state === "insert";
        if (aInsert && bInsert) return 0;
        if (aInsert) return 1;
        if (bInsert) return -1;

        if (original) {
          return original(valueA, valueB, nodeA, nodeB, isInverted);
        }
        return defaultCompare(valueA, valueB);
      },
    };
  };

  const lastRowNodeId = useRef<string | null>(null); // 26.03.13 kdy H-L-D 구조등의 그리드 필터 다시걸어줄 때 Id값 비교용
  const isapplyLikeFilter = useRef<boolean>(false); // 26.03.19 kdy applyLikeFilters안에서 퀵필터 예외컬럼 등록 중복 안되게
  const fixCheckbox = useRef<boolean>(false);
  const lastFilterConditions = useRef<{ [key: string]: any }>({}); // 26.04.29 kdy H-L-D 구조등의 그리드 채번시 필터가 다시 안도는 문제 때문에 추가
  //#endregion

  //#region 컬럼 정의 설정
  useEffect(() => {
    const loadColumnDefs = async () => {
      let updatedColumnDefs:
        | any[]
        | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];

      if (memoizedColumnConfig.length > 0) {
        setSeq(Number(memoizedColumnConfig[0].cd_grid.replace("grid", "")));
        const generatedColumnDefs = await SnColumnsCreate(
          memoizedColumnConfig,
          etcProps?.isEditable ?? true
        );
        // updatedColumnDefs = generatedColumnDefs;
        updatedColumnDefs = generatedColumnDefs.map((colDef) =>
          ime.applyToColumn(
            wrapComparator({
              ...colDef,
              mainMenuItems: mainMenuItems,
            })
          )
        );
        if (etcProps?.ischeckboxSelection) {
          updatedColumnDefs = [
            {
              headerName: "",
              colId: "fixCheckbox",
              field: "fixCheckbox",
              width: 40,
              lockPosition: "left", // 다른 컬럼이 이 앞으로 이동 불가
              lockPinned: true, // 헤더 메뉴로 고정 해제 불가
              suppressMovable: true, // 이동 불가

              // ❗ editable 끔 (중복 이벤트 방지)
              editable: false,
              suppressClickEdit: true,

              // ❗ 기존 selector / editor 전부 제거
              cellRenderer: (params: any) => {
                if (params.node?.rowPinned) return null;

                const checked = params.value === true;
                return (
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      minWidth: 16,
                      minHeight: 16,
                      flexShrink: 0,
                      border: "1px solid #9e9e9e",
                      borderRadius: 2,
                      backgroundColor: checked ? "#2196f3" : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxSizing: "border-box",
                    }}
                  >
                    {checked && (
                      <svg width="12" height="12" viewBox="0 0 12 12">
                        <polyline
                          points="2,5 5,8 10,3"
                          style={{
                            fill: "none",
                            stroke: "#fff",
                            strokeWidth: 2,
                          }}
                        />
                      </svg>
                    )}
                  </div>
                );
              },

              headerComponent: CustomHeaderCheckbox,

              cellStyle: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              },

              mainMenuItems: mainMenuItems,
            },
            ...updatedColumnDefs,
          ];
        }
      }

      const columnDefsWithRowNumber = [
        {
          headerName: "No.", // 순번 컬럼
          valueGetter: (params: {
            node: { rowPinned: any; rowIndex: number };
          }) => {
            if (params.node.rowPinned) {
              if (params.node.rowPinned === "top") return "";
              const visibleRowCount =
                gridRef.current?.api.getDisplayedRowCount() || 0;
              return visibleRowCount;
            }
            return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
          },
          colId: "fixIndicator",
          width: 70, // 너비
          pinned: "left", // 왼쪽 고정
          lockPosition: "left", // 다른 컬럼이 이 앞으로 이동 불가
          lockPinned: true, // 헤더 메뉴로 고정 해제 불가
          cellClass: "row-number-cell", // 스타일 클래스
          suppressMovable: true, // 이동 불가
          suppressNavigable: true, // 키보드 이동(탭, 방향키) 차단
          sortable: false, // 정렬 불가
          filter: false, // 필터링 불가
          rowDrag: !!etcProps?.rowDragOrderKey, // 순서변경용 드래그 핸들
          mainMenuItems: mainMenuItems,
        },
        ...updatedColumnDefs, // 기존 컬럼 정의
        {
          field: "block",
          hide: true,
        },
        // 순서변경 키 컬럼이 메타에 없으면 숨김으로 보장한다 — handleInternalRowDragEnd 의
        // setDataValue/정렬이 동작하려면 해당 field 의 colDef 가 존재해야 한다.
        ...(etcProps?.rowDragOrderKey &&
        !updatedColumnDefs.some(
          (c: any) => (c.field ?? c.colId) === etcProps.rowDragOrderKey
        )
          ? [{ field: etcProps.rowDragOrderKey, hide: true }]
          : []),
      ];
      setColumnDefs(columnDefsWithRowNumber);
      // 26.09.22 kdy 서머리 행은 yn_sum 컬럼이 없어도 항상 표시(건수만 노출),
      // etcProps.showFooter === false 일 때만 숨긴다.
      if (etcProps?.showFooter !== false) {
        setInitPinnedBottomRowData([
          {
            fixIndicator: 0,
          },
        ]);
      } else {
        setInitPinnedBottomRowData([]);
      }
    };
    loadColumnDefs();
    if (!isAddContext && memoizedColumnConfig.length > 0) {
      setIsAddContext(true);
      const laodContextMenu = async () => {
        const params = {
          queryId: "getContextMeneu",
          id: "CommonMapper",
          params: {
            cd_menu: memoizedColumnConfig[0]?.cd_menu || "",
            cd_firm: userInfo.cd_firm,
            cd_ctrl: memoizedColumnConfig[0]?.cd_grid || "",
          },
        };
        const data: any = await fnGetDataSet(params);
        if (!data || data.length === 0) {
          setMenuItems([]);
          return;
        }
        const menuItems = data[0].map((item: any) => ({
          name: item.nm_contextmenu,
          group: item.no_group,
          action: () => {
            handleContextMenu(item);
          },
        }));
        setMenuItems(menuItems);
      };
      laodContextMenu();
    }
  }, [memoizedColumnConfig, etcProps?.showFooter]);

  // AG Grid 컬럼 등록 확인
  const tryResolveColumnsReady = () => {
    if (!gridApiRef.current || memoizedColumnConfig.length === 0) return;
    const firstBizCol = memoizedColumnConfig[0]?.cd_column;
    if (firstBizCol && gridApiRef.current.getColumn(firstBizCol)) {
      columnsReadyResolveRef.current?.();
    }
  };

  useEffect(() => {
    tryResolveColumnsReady();
  }, [columnDefs, memoizedColumnConfig]);

  //#endregion

  // 26.08.21 kdy 언마운트 시 예약된 0행 포커스 보정 취소
  useEffect(() => {
    return () => {
      if (initialFocusRaf.current !== null) {
        cancelAnimationFrame(initialFocusRaf.current);
        initialFocusRaf.current = null;
      }
    };
  }, []);

  //#region
  useEffect(() => {
    initTotals();

    setKeyRowData(
      (memoizedRowData || []).map((row) => {
        const key = row.KeyField || generateKey();

        return etcProps?.ischeckboxSelection
          ? { ...row, KeyField: key, fixCheckbox: false, block: false }
          : { ...row, KeyField: key, block: false };
      })
    );
    // 26.08.21 kdy rowData가 그리드에 반영되기 "전"에 기본 정렬을 확정한다.
    // 반영 후에 걸면 재정렬로 modelUpdated가 한 번 더 발생해 0행 스크롤 보정과 경합한다.
    applyInitialSort();
    setIsMemoizedRowDataChanged(true);
  }, [memoizedRowData]);

  //#endregion

  //#region 공통메서드
  const generateKey = () => {
    let key;
    do {
      key = uuidv4(); // 항상 새로운 UUID 생성
    } while (usedKeys.has(key)); // 중복 여부 확인
    usedKeys.add(key);
    return key;
  };

  const handleRowFocusAfterDelete = (deletedRowIndex: number) => {
    if (!gridApiRef.current) return;
    const totalRowCount = gridApiRef.current.getDisplayedRowCount();

    if (totalRowCount === 0) return;

    let nextFocusIndex = deletedRowIndex;

    // 1. 삭제된 인덱스가 현재 총 행 수를 초과하는 경우 (마지막 행을 삭제한 경우)
    if (deletedRowIndex >= totalRowCount) {
      // 남아있는 가장 마지막 행으로 포커스
      nextFocusIndex = totalRowCount - 1;
    }
    // 2. 삭제된 인덱스가 유효한 경우 (그 아래 행이 남아있는 경우)
    else {
      // 삭제된 행의 자리로 포커스를 이동합니다.
      nextFocusIndex = deletedRowIndex;
    }

    // 포커스/선택 초기화 (이벤트 핸들러에서 수행하는 것이 더 안전합니다)
    gridApiRef.current.clearFocusedCell();
    gridApiRef.current.deselectAll();

    // 포커스 설정
    const rowNode = gridApiRef.current.getDisplayedRowAtIndex(nextFocusIndex);
    const firstCol = gridApiRef.current.getAllDisplayedColumns()[0];

    if (rowNode && firstCol) {
      gridApiRef.current.ensureIndexVisible(nextFocusIndex);
      gridApiRef.current.setFocusedCell(nextFocusIndex, firstCol.getColId());
      rowNode.setSelected(true);
    }
  };

  const handleModifiedRowsDelete = (deletedRows: RowData[]) => {
    deletedRows.forEach((row) => {
      // '추가'된 행이면 -> 'added' 목록에서 제거
      const addedIndex = modifiedRowsRef.current.added.findIndex(
        (r) => r.KeyField === row.KeyField
      );
      if (addedIndex !== -1) {
        modifiedRowsRef.current.added.splice(addedIndex, 1);
      } else {
        const updatedIndex = modifiedRowsRef.current.updated.findIndex(
          (r) => r.KeyField === row.KeyField
        );
        if (updatedIndex !== -1) {
          modifiedRowsRef.current.updated.splice(updatedIndex, 1);
        }
        modifiedRowsRef.current.deleted.push(row);
      }
    });
  };

  // ISS2026060190 요청으로 최대 최솟값 추가
  const calculateTotals = async () => {
    const aggCols = columnConfig.filter((col: any) =>
      ["Y", "H", "L"].includes(col.yn_sum)
    );

    // 26.09.22 kdy 합계 컬럼이 없어도 건수(fixIndicator) 갱신을 위해 계속 진행한다.
    if (!gridApiRef.current) return;

    const totalRow: Record<string, any> = {};
    const columnTypes: Record<string, string> = {};

    const getColumnType = (col: any) => {
      switch (col.tp_column) {
        case "SnDateBox":
          return "date";
        case "SnNumberBox":
          return "number";
        default:
          return "string";
      }
    };

    const parseDateToNumber = (value: any) => {
      if (value === null || value === undefined || value === "") return null;

      const str = String(value).trim();

      if (/^\d{8}$/.test(str)) {
        const y = +str.slice(0, 4);
        const m = +str.slice(4, 6) - 1;
        const d = +str.slice(6, 8);
        return new Date(y, m, d).getTime();
      }

      if (/^\d{4}[\/-]\d{2}[\/-]\d{2}$/.test(str)) {
        const [y, m, d] = str.split(/[\/-]/).map(Number);
        return new Date(y, m - 1, d).getTime();
      }

      const dt = new Date(str);
      return isNaN(dt.getTime()) ? null : dt.getTime();
    };

    const parseValue = (value: any, type: string) => {
      if (value === null || value === undefined || value === "") return null;

      if (type === "number") {
        const n = Number(value);
        return isNaN(n) ? null : n;
      }

      if (type === "date") {
        return parseDateToNumber(value);
      }

      return value;
    };

    aggCols.forEach((col) => {
      const type = getColumnType(col);
      columnTypes[col.cd_column] = type;

      switch (col.yn_sum) {
        case "H":
          totalRow[col.cd_column] = Number.NEGATIVE_INFINITY;
          break;

        case "L":
          totalRow[col.cd_column] = Number.POSITIVE_INFINITY;
          break;

        default:
          totalRow[col.cd_column] =
            type === "number" || type === "date" ? 0 : null;
          break;
      }
    });

    gridApiRef.current.forEachNodeAfterFilter((node: any) => {
      const row = node.data;

      if (events?.sumRowFilter && !events.sumRowFilter(row)) return;

      aggCols.forEach((col) => {
        const key = col.cd_column;
        const type = columnTypes[key];

        const value = parseValue(row[key], type);
        if (value === null) return;

        const current = totalRow[key];

        switch (col.yn_sum) {
          case "H":
            totalRow[key] = Math.max(current, value);
            break;

          case "L":
            totalRow[key] = Math.min(current, value);
            break;

          default:
            if (type === "number") {
              totalRow[key] += value;
            } else if (type === "date") {
              totalRow[key] = Math.max(current, value);
            } else if (type === "string") {
              if (!totalRow[key]) totalRow[key] = value;
            }
            break;
        }
      });
    });

    aggCols.forEach((col) => {
      const key = col.cd_column;
      const type = columnTypes[key];

      if (
        totalRow[key] === Number.NEGATIVE_INFINITY ||
        totalRow[key] === Number.POSITIVE_INFINITY
      ) {
        totalRow[key] = null;
      }

      if (type === "date" && totalRow[key]) {
        const d = new Date(totalRow[key]);

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");

        totalRow[key] = `${yyyy}-${mm}-${dd}`;
      }
    });

    // 합계를 화면에서 재정의해야 하는 경우 (예: 재고수량 = 누적값의 합이 아닌 최종 재고수량)
    const finalRow = events?.onBeforeSetFooter
      ? (events.onBeforeSetFooter(totalRow) ?? totalRow)
      : totalRow;

    const pinnedNode = gridApiRef.current.getPinnedBottomRow(0);

    if (pinnedNode) {
      pinnedNode.setData(finalRow);

      gridApiRef.current.refreshCells({
        rowNodes: [pinnedNode],
        columns: ["fixIndicator"],
        force: true,
      });
    }

    // const result = [totalRow];
    // gridApiRef.current.setGridOption("pinnedBottomRowData", result);
    // gridApiRef.current.refreshCells({
    //   force: true, // valueGetter/valueFormatter를 강제로 다시 실행합니다.
    //   columns: ["fixIndicator"], // 순번 컬럼의 colId 사용
    // });
  };

  const initTotals = async () => {
    const sums = columnConfig.filter((col) => col.yn_sum === "Y");

    if (!gridApiRef.current || sums.length === 0) return;

    const totalRow: Record<string, number> = {};

    sums.forEach((sumCol) => {
      totalRow[sumCol.cd_column] = 0;
    });

    const pinnedNode = gridApiRef.current.getPinnedBottomRow(0);
    if (pinnedNode) {
      pinnedNode.setData(totalRow);

      gridApiRef.current.refreshCells({
        rowNodes: [pinnedNode],
        force: true,
      });
    }

    // const result = [totalRow];
    // gridApiRef.current.setGridOption("pinnedBottomRowData", result);
    // gridApiRef.current.refreshCells({
    //   force: true, // valueGetter/valueFormatter를 강제로 다시 실행합니다.
    //   columns: ["fixIndicator"], // 순번 컬럼의 colId 사용
    // });
  };

  const checkEditable = (rowData: any, colDef: any): boolean => {
    const isEditable =
      typeof colDef.editable === "function"
        ? colDef.editable({
            data: rowData,
            colDef: colDef,
          })
        : true;
    return isEditable;
  };

  const moveFocusToNextEditableCell = (data: any, column: Column) => {
    if (!gridApiRef.current || !column) return;
    if (!gridApiRef.current.getFocusedCell()) return;
    const isCurrentCellEditable = checkEditable(data, column.getColDef());

    if (!column.getColId().startsWith("no_")) {
      if (isCurrentCellEditable) {
        return;
      }
    }

    if (!checkLastColumn(column.getColId())) {
      const moved = gridApiRef.current.tabToNextCell();
      const nextFocusedCell = gridApiRef.current.getFocusedCell();
      if (moved) {
        if (nextFocusedCell)
          requestAnimationFrame(() => {
            moveFocusToNextEditableCell(data, nextFocusedCell.column);
          });
      }
    } else {
      if (etcProps?.entertoAddrow) {
        const focusedCell = gridApiRef.current.getFocusedCell();

        if (focusedCell) {
          const rowIndex = focusedCell.rowIndex;
          const lastRowIndex = gridApiRef.current.getDisplayedRowCount() - 1;

          if (rowIndex === lastRowIndex) {
            addRow();
          }
        }
      }
    }
  };

  const moveToNextCell = (data: any, colId: string) => {
    if (!checkLastColumn(colId)) {
      const moved = gridApiRef.current.tabToNextCell();

      if (moved) {
        requestAnimationFrame(() => {
          moveFocusToNextEditableCell(
            data,
            gridApiRef.current.getFocusedCell().column
          );
        });
      }
    } else {
      if (etcProps?.entertoAddrow) {
        const focusedCell = gridApiRef.current.getFocusedCell();

        if (focusedCell) {
          const rowIndex = focusedCell.rowIndex;
          const lastRowIndex = gridApiRef.current.getDisplayedRowCount() - 1;

          if (rowIndex === lastRowIndex) {
            addRow();
          }
        }
      }
    }
  };
  // 도움창에서 값을 골랐으면 true (취소·닫기·못 띄움은 false)
  const openPopup = async (params: any): Promise<boolean> => {
    if (isPopupOpen.current) return false; // 중복 실행 방지
    if (!gridApiRef.current) return false;
    // 조회·저장으로 행이 빠진 뒤에 불리면 노드가 없다
    const rowNode = params.api.getRowNode(params.node.id);
    if (!rowNode) return false;
    const isCurrentCellEditable = checkEditable(rowNode.data, params.colDef);
    if (!isCurrentCellEditable) {
      return false;
    }
    isPopupOpen.current = true;
    try {
      const { applyNodes, isMove } = await PopupHelper(
        gridApiRef,
        params,
        popupCons.current,
        columnConfig,
        addRow
      );
      gridApiRef.current.stopEditing();
      if (isMove) {
        const len = applyNodes.length;
        moveToNextCell(applyNodes[len - 1].data, params.colDef.field);
      }
      events?.onAfterPopupClose &&
        events.onAfterPopupClose({
          nodes: applyNodes,
          colId: params.colDef.field,
        });
      return isMove;
    } finally {
      isPopupOpen.current = false; // 오류가 나도 해제되도록
    }
  };

  const checkLastColumn = (colId: string) => {
    const displayedCols = gridApiRef.current.getAllDisplayedColumns();
    const isLastDisplayed =
      displayedCols[displayedCols.length - 1].getColId() === colId;
    return isLastDisplayed;
  };
  // 그룹 컬럼(children)까지 펼쳐서 colId 로 찾을 수 있게 한다.
  const flattenColDefs = (defs: any[]): any[] =>
    (defs ?? []).flatMap((def: any) =>
      def?.children ? flattenColDefs(def.children) : [def]
    );

  const columnSave = async () => {
    if (!gridApiRef.current) return;

    // 1. 실시간 상태(너비, 가시성, 순서) 정보를 가져옵니다.
    //    fixIndicator/fixCheckbox 등 내부 전용 컬럼과 히든 block 컬럼은
    //    sys_formgridcolumn에 저장되면 안 되므로 제외한다.
    const colState = gridApiRef.current
      .getColumnState()
      .filter(
        (state: any) =>
          !state.colId?.startsWith("fix") && state.colId !== "block"
      );
    if (colState.length === 0) return;

    // 2. 원본 설정 정보(headerName 등)를 가져옵니다.
    //    그룹 컬럼은 getColumnDefs() 가 children 을 가진 그룹 정의로 내려주고,
    //    colDef 에 colId 가 없는 경우도 있어 평탄화 후 colId/field 로 매칭한다.
    const colDefs = flattenColDefs(
      (gridApiRef.current.getColumnDefs() as any[]) ?? []
    );
    const findDef = (colId: string) =>
      colDefs.find((def: any) => (def?.colId ?? def?.field) === colId);
    const findConfig = (colId: string) =>
      (columnConfig ?? []).find((config: any) => config.cd_column === colId);

    const saveUser = userInfo.cd_user === "SYSTEM" ? "admin" : userInfo.cd_user;

    const saveConfig = colState.map((state: any, index: number) => {
      const originalDef = findDef(state.colId);
      const config: any = findConfig(state.colId) ?? {};

      return {
        // 신규(insert)로 들어갈 수 있으므로 원본 설정값(tp_column 등 NOT NULL 컬럼 포함)을
        // 그대로 깔고, 화면에서 바뀐 값만 덮어쓴다.
        ...(originalDef?.columnConfig ?? {}),
        ...config,
        cd_firm: config.cd_firm || userInfo.cd_firm,
        cd_menu:
          originalDef?.columnConfig?.cd_menu ||
          config.cd_menu ||
          columnConfig?.[0]?.cd_menu ||
          "",
        cd_user: saveUser,
        cd_grid:
          originalDef?.columnConfig?.cd_grid ||
          config.cd_grid ||
          columnConfig?.[0]?.cd_grid ||
          "",
        cd_column: state.colId,
        nm_column: originalDef?.headerName || config.nm_column || "",
        // state.hide는 정확히 true/false로 나옵니다.
        // 컬럼선택 목록에도 안 나오는 숨김(H)은 그대로 유지한다.
        fg_visible:
          state.hide === true ? (config.fg_visible === "H" ? "H" : "N") : "Y",
        // state의 순서가 현재 화면 순서이므로 index를 그대로 no_seq로 사용
        no_seq: index,
        qt_width: state.width,
      };
    });

    // 내 설정 행이 아직 없는 컬럼은 update 로는 아무것도 안 바뀐다.
    // (공용/admin 설정을 보고 있거나, 나중에 추가된 컬럼) 그런 컬럼은 insert 로 보낸다.
    // 설정에 cd_user 자체가 없으면 판단이 불가하므로 기존대로 update 만 보낸다.
    const hasUserField = (columnConfig ?? []).some(
      (config: any) => config.cd_user !== undefined && config.cd_user !== null
    );

    const insert: any[] = [];
    const update: any[] = [];
    saveConfig.forEach((row: any) => {
      const config: any = findConfig(row.cd_column) ?? {};
      if (!hasUserField || config.cd_user === saveUser) {
        update.push(row);
      } else {
        insert.push(row);
      }
    });

    const SaveData = {
      formgridcolumn: { insert, update, delete: [] },
    };

    await fnSave("SN_SYS_BAS_REG_007_SAVE_01", SaveData);
  };
  const columnInit = async () => {
    const params = {
      queryId: "getInitColumn",
      id: "CommonMapper",
      params: {
        cd_menu: columnConfig[0]?.cd_menu || "",
        cd_user: userInfo.cd_user,
        cd_grid: columnConfig[0]?.cd_grid || "",
      },
    };
    const rtn: any[] = await fnGetDataSet(params);
    const columnconfig = rtn[0];
    const fixedPrefixState: any[] = [];
    if (etcProps?.ischeckboxSelection) {
      fixedPrefixState.push({ colId: "fixCheckbox", hide: false, width: 20 });
    }
    fixedPrefixState.push({ colId: "fixIndicator", hide: false, width: 70 });

    const configState = columnconfig
      .map((config: any) => {
        const actualColumn = gridApiRef.current.getColumn(config.cd_column);
        if (actualColumn) {
          return {
            colId: config.cd_column,
            hide: config.fg_visible === "N",
            width: config.qt_width || 150,
          };
        }
        return null;
      })
      .filter((s: any) => s !== null);

    const finalState = [...fixedPrefixState, ...configState];
    gridApiRef.current.applyColumnState({
      state: finalState,
      applyOrder: true,
    });
    const currentColumnDefs =
      gridApiRef.current.getColumnDefs() as CustomColDef[];

    if (currentColumnDefs) {
      const updatedColumnDefs = currentColumnDefs.map((colDef) => {
        const result = columnconfig.find(
          (r: any) => r.cd_column === (colDef.colId || colDef.field)
        );

        if (result && result.nm_column) {
          return {
            ...colDef,
            headerName: result.nm_column, // DB의 컬럼명으로 교체
          };
        }
        return colDef;
      });

      gridApiRef.current.setGridOption("columnDefs", updatedColumnDefs);
    }
  };
  const headerChange = async () => {
    if (!gridRef.current) return;

    const currentColumnDefs =
      gridRef.current?.api.getColumnDefs() as CustomColDef[];
    const results: any[] | string = await showPopupModal({
      title: "항목명변경",
      children: <HeaderPopup dataSource={currentColumnDefs as any[]} />,
      width: "300px",
      height: "550px",
    });
    if (!results || results.length === 0 || results === "close") return;
    const updatedResults = results as any[];

    const updatedColumnDefs: CustomColDef[] = currentColumnDefs.map(
      (colDef) => {
        const result = updatedResults.find((r) => r.field === colDef.colId);

        if (result && result.headerName !== undefined) {
          return {
            ...colDef,
            headerName: result.headerName,
          };
        }
        return colDef;
      }
    );

    // 4. 반영
    gridRef.current.api.setGridOption("columnDefs", updatedColumnDefs);
  };

  const enableRowDrag = () => {};
  //#endregion

  //#region 외부에서 접근 가능한 메서드 정의
  useImperativeHandle(ref, () => ({
    addRow,
    whenColumnsReady: () => columnsReadyPromiseRef.current!, // 그리드 컬럼 세팅 완료 여부 확인
    clearCheckBox, // 26.02.05 kdy 체크박스 선택 항목 초기화
    deleteSelectedRows,
    deleteRowByIndex,
    deleteAllRows,
    getChange,
    resetUpdate,
    refreshFooter, // 26.02.05 kdy 푸터 갱신
    selectAndFocusRow,
    selectAllFiltered,
    getSelectedRows,
    getRangeSelectedRows,
    getCheckedRowCount: () => {
      let cnt = 0;
      gridApiRef.current?.forEachNode((node: RowNode) => {
        if (!node.rowPinned && node.data.fixCheckbox) {
          cnt++;
        }
      });
      return cnt;
    }, // 26.04.30 kdy 체크박스 체크된 RowCount 반환
    getFilteredCheckedRowCount: () => {
      let cnt = 0;
      gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
        if (!node.rowPinned && node.data.fixCheckbox) {
          cnt++;
        }
      });
      return cnt;
    }, // 26.05.13 kdy 필터링된 데이터 체크박스 체크된 RowCount 반환
    getFocusedRowChecekd: () => {
      if (!focusedRowNode?.rowPinned && focusedRowNode?.data.fixCheckbox) {
        return true;
      } else {
        return false;
      }
    }, // 26.05.13 kdy 포커스노드 체크여부 반환
    getDisplayedRowCount: () => {
      return gridApiRef.current?.getDisplayedRowCount() || 0;
    },
    getRowCount: () => {
      let count = 0;
      gridApiRef.current.forEachNode(() => {
        count++;
      });
      return count;
    }, // 26.02.11 kdy 필터 안된 전체 RowCount 반환
    stopEditing: () => gridApiRef.current.stopEditing(),
    changeColumnConfigs,
    changeColumnReorderConfigs, // 26.02.02 kdy 컬럼 순서 정보 바뀔 경우가 있어 추가
    requiredValuesCheck,
    applyBlock,
    clearBlock,
    getMaxValue,
    getNonFilterMaxValue, // 26.02.02 kdy 항번이 pk때문에만 필요한 경우가 있어 추가
    getCheckedRows,
    applyFilters,
    applyNoMatchFilter, // 26.09.23 kdy 상위 그리드 필터 결과가 0행일 때 하위 체인을 비우는 용도
    applyLikeFilters, // 26.02.05 kdy 바코드 스캔에서 필요해서 like 필터 추가
    resetFilters,
    // 챗봇 스냅샷용 — applyFilters로 걸린 부모-자식 관계 키(no_so 등)를 gridRefs 경로에도 노출.
    // 이게 없으면 숨김 FK가 스냅샷에서 잘려 상단×하단 결합 집계(조인)가 불가능해진다.
    getChatFilters: () => chatFiltersRef.current,
    getFooterData,
    getFlatDataFromGrid,
    applyPopupConditions: (popupConditions: any) =>
      (popupCons.current = popupConditions),
    sharedCellValueChanged,
    // ref 우선(프로그램 포커스가 같은 tick에 읽히도록), 없으면 state 폴백.
    focusedRowNode: () => (focusedRowNodeRef.current ?? focusedRowNode)!,
    getRowNodeAtIndex: (Index: number) =>
      gridApiRef.current.getDisplayedRowAtIndex(Index),
    getRowNode: (key: string) => gridApiRef.current.getRowNode(key),
    getAllGridColumns: () => gridApiRef.current.getAllGridColumns(),
    autoSizeAllColumns: (skipHeader?: boolean) => {
      if (!gridApiRef.current) return;
      const columns = gridApiRef.current.getAllDisplayedColumns?.() || [];
      const colIds = columns
        .map((col: any) => col?.getColId?.())
        .filter((id: any) => !!id);
      if (colIds.length < 1) return;
      gridApiRef.current.autoSizeColumns(colIds, skipHeader ?? false);
    },
    sizeColumnsToFit: () => {
      if (!gridApiRef.current) return;
      gridApiRef.current.sizeColumnsToFit();
    },
    calculateTotals,
    resetGrid,
    forEachFilteredNode: async (callback: (node: RowNode) => void) => {
      gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
        callback(node); // 여기서 바로 처리
      });
    },
    changeGroupColumnConfigs,
    scrollToIndex: (index: number) =>
      gridApiRef.current.ensureIndexVisible(index),
    setFocusedCell: (index: number, colId: string) =>
      gridApiRef.current.setFocusedCell(index, colId),
    forEachNode: (callback: (node: RowNode) => void) => {
      gridApiRef.current?.forEachNode((node: RowNode) => {
        callback(node); // 여기서 바로 처리
      });
    },
    clearFocus: () => {
      lastRowNodeId.current = "reset_last_rowNode_id"; // 26.03.13 kdy H-L-D 구조 H이동시 D에걸린 L 필터 다시돌게
      gridApiRef.current.clearFocusedCell();
      gridApiRef.current.deselectAll();
    },
    getAllDisplayedColumns: () => gridApiRef.current.getAllDisplayedColumns(),
    exportDataAsExcel: (params: any) =>
      gridApiRef.current.exportDataAsExcel(buildExcelExportParams(params)),
    exportMultipleSheetsAsExcel: (params: any) =>
      gridApiRef.current.exportMultipleSheetsAsExcel(params),
    getSheetDataForExcel: (params: any) =>
      gridApiRef.current.getSheetDataForExcel(params),
    applyTransaction: (params: any) =>
      gridApiRef.current.applyTransaction(params),
    setCheckedAll,
    setCheckedAllWithFilter,
    setCheckedFocusedRow: (check: boolean, source?: string) =>
      focusedRowNode?.setDataValue("fixCheckbox", check, source),
    setValueImmediately,
    setCheckedRow: (node: RowNode, check: boolean, source?: string) =>
      node.setDataValue("fixCheckbox", check, source),
    reverseCheckedRow: (node: RowNode, source?: string) =>
      node.setDataValue("fixCheckbox", !node.data.fixCheckbox, source),
    getCheckedFilteredRows,
    getFilteredData,
    getSortModel: () =>
      (gridApiRef.current?.getColumnState() || [])
        .filter((col: any) => col.sort)
        .sort((a: any, b: any) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((col: any) => ({ colId: col.colId, sort: col.sort })),
    onSortChanged,
    onFilterChanged,
    deleteRowByNode: (node: RowNode) => {
      gridApiRef.current?.applyTransaction({ remove: [node.data] });
      // 26.03.25 kdy getchange타게 추가
      handleModifiedRowsDelete([node.data]);
      setRecoveryData({ rows: node.data, indexes: node.rowIndex! });
      calculateTotals();
    },
  }));

  const addRow = (
    newRow?: RowData,
    useEvent?: boolean,
    preferNewRow?: boolean,
    addIndex?: number
  ): RowNode | null => {
    if (!gridApiRef.current) return null;

    // 1. 새 행 데이터 준비
    let baseRow: any = newRow || {};
    // preferNewRow(챗봇 복제): baseRow === newRow(같은 참조)라 아래 onBeforeAddRow 훅이 newRow를 직접
    // 변형한다. 그래서 훅 실행 전에 복제값을 캡처해 두고, 훅·컬럼기본값 적용 후 마지막에 다시 덮어
    // "복제값이 최종 승리"하게 한다(um 등 훅이 0으로 바꾸던 필드 복원). 키는 복제본에 없어 훅값이 유지됨.
    const preferred =
      preferNewRow && newRow
        ? { ...(newRow as Record<string, unknown>) }
        : null;

    //  화면에서 넘긴 onBeforeAddRow 훅 있을 때만 실행
    if (events?.onBeforeAddRow && useEvent != false) {
      const rtn = events.onBeforeAddRow();
      if (typeof rtn === "boolean" && rtn === false) {
        return null;
      }
      if (typeof rtn === "object" && rtn) {
        Object.assign(baseRow, rtn);
      }
    }
    const combos = columnConfig.filter((col) => col.tp_column == "SnComboBox");
    combos.map((combo) => {
      Object.entries(baseRow)
        .filter(([_, value]) => value === "top1")
        .map(([key]) => {
          if (key == combo.cd_column) {
            const dataSource = Array.isArray(combo.js_combo)
              ? combo.js_combo
              : typeof combo.js_combo === "string" &&
                  combo.js_combo.trim() !== ""
                ? JSON.parse(combo.js_combo)
                : [];
            // 26.03.16 kdy 콤보데이터 없으면 행추가 안돼서 수정
            if (
              dataSource &&
              dataSource.length > 0 &&
              dataSource[0]["code"] !== undefined
            ) {
              baseRow[key] = dataSource[0]["code"];
            } else {
              baseRow[key] = "";
            }
          }
        });
    });

    // 26.06.04 kdy 기본값 설정 있으면 추가시 값 넣어주기
    columnConfig.forEach((col) => {
      if (
        col.cd_value !== undefined &&
        col.cd_value !== null &&
        col.cd_value !== ""
      ) {
        baseRow[col.cd_column] = col.cd_value;
      }
    });

    // 복제값을 최종 적용(훅·컬럼기본값보다 우선) — 챗봇 복제에서 훅이 덮은 업무값(um 등) 복원.
    if (preferred) {
      Object.assign(baseRow, preferred);
    }
    const newKey = generateKey();
    const newData = {
      ...baseRow,
      KeyField: baseRow.KeyField ?? newKey,
      state: "insert",
      block: false,
    };
    // 수정된 행 추적
    modifiedRowsRef.current.added.push(newData);
    // 2. applyTransaction 호출 및 결과 객체 받기
    // 26.10.12 addIndex(원본 데이터 순서 기준 위치)를 주면 그 자리에 끼워 넣고, 없으면 기존처럼 맨 끝에 붙인다.
    const transactionResult = gridApiRef.current.applyTransaction({
      add: [newData],
      ...(addIndex != null && addIndex >= 0 ? { addIndex } : {}),
    });
    calculateTotals();

    // 3. 트랜잭션 결과에서 새로 추가된 RowNode 확인
    const newRowNodes = transactionResult?.add;
    if (newRowNodes && newRowNodes.length > 0) {
      // 새로 추가된 행의 RowNode를 얻습니다. (일반적으로 첫 번째 항목)
      const addedRowNode = newRowNodes[0];
      // 새로 추가된 행의 인덱스를 가져옵니다.
      const newIndex = addedRowNode.rowIndex as number;
      //gridApiRef.current.tabToNextCell();
      selectAndFocusRow(newIndex);
      if (etcProps?.isEditable === false) return addedRowNode;
      const firstEditableCol = gridApiRef.current
        .getAllDisplayedColumns()
        .find((col: any) => {
          const colId = col.getColId();
          return (
            !colId.startsWith("no_") &&
            colId !== "fixIndicator" &&
            checkEditable(newData, col.getColDef())
          );
        });

      //  행 추가 시 편집 가능한 컬럼이 없는 경우 firstEditableCol이 undefined가 되어
      //  getColId() 호출에서 에러가 발생할 수 있으므로 null 체크 후 바로 반환하도록 방어 로직 코드 추가
      if (!firstEditableCol) {
        events?.onAfterAddRow && events?.onAfterAddRow(addedRowNode);
        return addedRowNode;
      }

      const colKey = firstEditableCol.getColId();
      requestAnimationFrame(() => {
        gridApiRef.current.ensureIndexVisible(newIndex);

        gridApiRef.current?.setFocusedCell(newIndex, colKey);
      });

      events?.onAfterAddRow && events?.onAfterAddRow(addedRowNode);
      return addedRowNode;
    }
    return null;
  };

  const clearCheckBox = () => {
    const updatedRows: any[] = [];

    gridApiRef.current.forEachNode((node: any) => {
      if (node.data && node.data.fixCheckbox === true) {
        node.data.fixCheckbox = false;
        updatedRows.push(node.data);
      }
    });
    if (updatedRows.length > 0) {
      gridApiRef.current.applyTransaction({ update: updatedRows });
    }
    gridApiRef.current.refreshHeader();
  };

  const deleteSelectedRows = () => {
    if (!gridApiRef.current) return;
    const selectedNodes = gridApiRef.current.getSelectedNodes();

    if (!gridApiRef.current || !selectedNodes || selectedNodes.length === 0) {
      return;
    }
    const rowsToRemove = selectedNodes.map((node: RowNode) => node.data);
    const rowsToRemoveIndexes = selectedNodes.map(
      (node: RowNode) => node.rowIndex
    );
    // 1. 삭제 후 포커스할 기준 인덱스 저장
    const firstSelectedIndex = selectedNodes[0]?.rowIndex || 0;

    // 2. 포커스 플래그 설정
    isDeleteFocusPending.current = {
      pending: true,
      index: firstSelectedIndex,
    };

    // 3. AG Grid에서 행 삭제 요청
    gridApiRef.current.applyTransaction({ remove: rowsToRemove });
    handleModifiedRowsDelete(rowsToRemove);
    setRecoveryData({ rows: rowsToRemove, indexes: rowsToRemoveIndexes });
    calculateTotals();
  };

  const deleteRowByIndex = (rowIndex: number) => {
    if (!gridApiRef.current) return;

    // 1. RowNode 가져오기
    const rowNode = gridApiRef.current.getDisplayedRowAtIndex(rowIndex);

    if (!rowNode || !rowNode.data) {
      return;
    }
    const rowToRemove = rowNode.data;
    const rowsToRemoveIndexes = rowNode.rowIndex;
    // 3. 포커스 플래그 설정
    // 삭제되기 전의 인덱스를 기준으로 포커스를 설정하도록 플래그에 저장
    isDeleteFocusPending.current = {
      pending: true,
      index: rowIndex, // 삭제된 행의 인덱스를 저장
    };

    // 4. AG Grid에서 행 삭제 요청
    gridApiRef.current.applyTransaction({ remove: [rowToRemove] });
    handleModifiedRowsDelete([rowToRemove]);
    setRecoveryData({ rows: [rowToRemove], indexes: [rowsToRemoveIndexes] });
    calculateTotals();
  };

  const deleteAllRows = () => {
    if (!gridApiRef.current) return;

    const rowsToRemove: any[] = [];
    const rowsToRemoveIndexes: number[] = [];

    gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
      rowsToRemove.push(node.data);
      rowsToRemoveIndexes.push(node.rowIndex!);
    });

    if (rowsToRemove.length === 0) {
      return;
    }
    gridApiRef.current.applyTransaction({ remove: rowsToRemove });
    handleModifiedRowsDelete(rowsToRemove);
    setRecoveryData({ rows: rowsToRemove, indexes: rowsToRemoveIndexes });
    calculateTotals();
  };

  // 26.11.04 kdy 상단 탭 제목을 Ctrl+Shift+더블클릭 하면 그리드 안의 데이터 보여주기
  const openDataInspector = () => {
    const api = gridApiRef.current;
    if (!api) return;
    const all: any[] = [];
    api.forEachNode((n: RowNode) => {
      if (!n.rowPinned) all.push(n.data);
    });
    const filtered: any[] = [];
    api.forEachNodeAfterFilterAndSort((n: RowNode) => {
      if (!n.rowPinned) filtered.push(n.data);
    });
    const change = getChange();
    const filterText = formatFilterModel(api.getFilterModel());
    showResizablePopupModal({
      title: `(전체 ${all.length}행 / 표시 ${filtered.length}행)${
        filterText ? ` ${filterText}` : ""
      }`,
      width: "1100px",
      height: "700px",
      okayBtn: false,
      closeBtnText: "닫기",
      rememberSize: true,
      rememberSizeKey: "snGridDataInspector",
      children: (
        <SnGridDataInspector
          columnConfig={columnConfig}
          datasets={{
            전체: all,
            표시중: filtered,
            추가: change.insert,
            수정: change.update,
            삭제: change.delete,
            컬럼정의: columnConfig,
          }}
        />
      ),
    });
  };

  const getChange = () => {
    return {
      modifiedRowcount:
        modifiedRowsRef.current.added.length +
        modifiedRowsRef.current.updated.length +
        modifiedRowsRef.current.deleted.length,
      insert: modifiedRowsRef.current.added,
      update: modifiedRowsRef.current.updated,
      delete: modifiedRowsRef.current.deleted,
    };
  };

  const resetUpdate = (isRecovery?: boolean) => {
    if (gridApiRef.current == null) return;
    if (isRecovery) {
      if (gridRef.current?.api == null) return;
      if (recoveryData.rows == null || recoveryData.indexes == null) return;
      const rows: any[] = recoveryData.rows;
      const indexes: any[] = recoveryData.indexes;
      rows.forEach((row, i) => {
        gridRef.current?.api.applyTransaction({
          add: [{ ...row }],
          addIndex: indexes[i],
        });
      });
      selectAndFocusRow(indexes[0]);
    } else {
      const updatedRows: any[] = [];

      gridApiRef.current.forEachNode((node: RowNode) => {
        const data = { ...node.data };
        delete data.state;
        const isUpdated = modifiedRowsRef.current.updated.find(
          (u) => u.KeyField === data.KeyField
        );
        if (isUpdated) {
          data.version = isUpdated.version + 1;
        }
        const isAdded = modifiedRowsRef.current.added.find(
          (a) => a.KeyField === data.KeyField
        );
        if (isAdded) {
          data.version = 0;
        }
        updatedRows.push(data);
      });
      gridApiRef.current.applyTransaction({ update: updatedRows });
    }
    modifiedRowsRef.current = { added: [], updated: [], deleted: [] };
  };

  const refreshFooter = () => {
    const footerData: any = {};
    const columns = gridApiRef.current.getAllGridColumns();

    columns.forEach((column: any) => {
      const colDef = column.getColDef();
      const field = colDef.field;

      if (field && (colDef.aggFunc === "sum" || colDef.summaryType === "sum")) {
        let sum = 0;
        gridApiRef.current.forEachNodeAfterFilter((node: any) => {
          if (node.data) {
            sum += Number(node.data[field] || 0);
          }
        });
        footerData[field] = sum;
      }
    });

    const pinnedNode = gridApiRef.current.getPinnedBottomRow(0);
    if (pinnedNode) {
      pinnedNode.setData(footerData);

      gridApiRef.current.refreshCells({
        rowNodes: [pinnedNode],
        force: true,
      });
    }
  };

  const getSelectedRows = () => {
    const selectedRows = gridApiRef.current?.getSelectedRows();
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  // 셀 범위(드래그) 선택이 덮은 행들의 data 를 중복 없이 반환. 행 선택(Ctrl/Shift+클릭)이
  // 아니라 마우스 드래그로 여러 건을 고를 때 쓴다.
  const getRangeSelectedRows = () => {
    const ranges = gridApiRef.current?.getCellRanges?.() ?? [];
    const byKey = new Map<string, any>();
    ranges.forEach((range: any) => {
      const start = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
      const end = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);
      for (let i = start; i <= end; i++) {
        const node = gridApiRef.current.getDisplayedRowAtIndex(i);
        if (node?.data && !node.rowPinned)
          byKey.set(node.data.KeyField, node.data);
      }
    });
    return [...byKey.values()];
  };

  const getFilteredData = () => {
    const data: any[] = [];
    gridApiRef.current.forEachNodeAfterFilterAndSort((node: RowNode) => {
      data.push(node.data);
    });
    return data;
  };

  /* 챗봇 행추가 — [+] 누르기는 그 순간의 화면 처리(events)를 부른다 (2026-10-15) */
  const chatEventsRef = useRef(events);
  chatEventsRef.current = events;
  /* 챗봇 행복사 중 — 새 행의 빈 코드 칸에 커서가 가도 도움창을 띄우지 않는다(챗봇이 곧 채운다). 사람이 쓸 때는 늘 false */
  const chatQuietRef = useRef(false);

  /** 코드 칸을 넣은 뒤 — 화면 연동(품명 · 규격 · 단위 · LOT여부 · 단가)이 행을 다 채울 때까지. 바뀌다 0.2초 조용하면 끝, 최대 2초 */
  const waitLinkedFill = async (node: RowNode) => {
    const snap = () => {
      try {
        return JSON.stringify(node.data);
      } catch {
        return "";
      }
    };
    const start = Date.now();
    let last = snap();
    let changed = false;
    let quietSince = Date.now();
    while (Date.now() - start < 2000) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      const now = snap();
      if (now !== last) {
        last = now;
        changed = true;
        quietSince = Date.now();
      } else if (changed && Date.now() - quietSince >= 200) {
        return;
      }
    }
  };
  const isCodeColumn = (colDef: any, data: any) => {
    const tp = getRowTpColumn(colDef, data);
    return (
      (tp === "SnCodeBox" || tp === "SnMultiCodeBox") &&
      !!colDef?.columnConfig?.cd_popup
    );
  };

  /* 메뉴 닫기 — 이 그리드의 수정 건수를 메뉴별로 알린다. 닫는 순간에만 불린다 (2026-10-16) */
  useEffect(() => {
    if (!chatMenuCode || !chatGridKey) return;
    const id = `grid:${chatGridKey}`;
    menuChangeStore
      .getState()
      .register(chatMenuCode, id, () => getChange().modifiedRowcount);
    return () => menuChangeStore.getState().unregister(chatMenuCode, id);
  }, [chatMenuCode, chatGridKey]);

  useEffect(() => {
    if (!chatMenuCode || !chatGridKey) return;

    onSearchStore
      .getState()
      .registerChatScreenGridDataProvider(chatMenuCode, chatGridKey, {
        /* 챗봇 행추가 · 행복사 (2026-10-15) — 행은 사람이 [+] 를 누른 것과 같은 길로 만든다.
           원본 · 바꿀 값 모두 사람이 입력하듯 넣는다 — 화면이 품명 · 단가 · 금액을 평소처럼 채운다(copyFocused · applyValues) */
        chatActions: {
          pressAdd: () => {
            const onAdd = chatEventsRef.current?.onBtnAddClick;
            if (typeof onAdd !== "function") return false;
            onAdd("addRow");
            return true;
          },
          focusedRow: () => focusedRowNodeRef.current,
          /* 고른 줄 — 체크박스 → 끌어 잡은 범위(여러 줄) → 커서 줄. 줄 선택이 한 줄만 되는 그리드가 많아
             여러 줄은 체크박스 · 범위로 고른다 — 그 둘을 안 보면 1줄만 복사됐다 (2026-10-16) */
          selectedRows: () => {
            const checked = getCheckedFilteredRows();
            if (checked.length > 0) return checked;
            const range = getRangeSelectedRows();
            if (range.length > 1) return range;
            return getSelectedRows() || [];
          },
          displayedRows: () => getFilteredData() || [],
          rowCount: () => gridApiRef.current?.getDisplayedRowCount?.() ?? 0,
          /* [+] 는 화면이 onBtnAddClick 을 줄 때만 그려진다(SnTabHeader) — 같은 기준 */
          canAdd: () =>
            typeof chatEventsRef.current?.onBtnAddClick === "function",
          copyFocused: async (
            source: Record<string, unknown>,
            exclude: string[] = []
          ) => {
            const node = focusedRowNodeRef.current;
            const api = gridApiRef.current;
            if (!node || !api) return [];
            const skip = new Set([
              "KeyField",
              "state",
              "block",
              "fixCheckbox",
              ...exclude,
            ]);
            const empty = (v: unknown) =>
              v === undefined || v === null || v === "";
            const copied: string[] = [];
            /* 보이는 칸만, 화면 순서대로 — 품목코드를 넣으면 화면이 품명 · 규격 · 단가를 채우고, 수량을 넣으면 금액을 계산한다.
               숨긴 칸 · 입력 불가 칸(상태 · 진행 수량 · 원인 번호)은 넣지 않는다. 메뉴마다 설정하지 않는다(사용자 2026-10-16) */
            for (const column of api.getAllDisplayedColumns?.() || []) {
              const colDef: any = column.getColDef();
              const field: string | undefined = colDef?.field;
              if (!field || skip.has(field)) continue;
              const value = source[field];
              if (empty(value)) continue;
              /* 화면이 새 행에 넣은 기본값 · 앞 칸이 채운 값(품목 → 단가표 단가)은 그대로 — 빈 칸(숫자 0 포함)만 원본으로 */
              const current = node.data?.[field];
              if (!empty(current) && current !== 0) continue;
              let editable = colDef.editable !== false;
              if (typeof colDef.editable === "function") {
                try {
                  editable = !!colDef.editable({
                    data: node.data,
                    node,
                    colDef,
                    column,
                    api,
                  });
                } catch {
                  editable = false;
                }
              }
              if (!editable) continue;
              node.setDataValue(field, value);
              copied.push(field);
              /* 코드 칸이면 화면 연동(품명 · 규격 · 단가)이 끝날 때까지 */
              if (isCodeColumn(colDef, node.data)) {
                await waitLinkedFill(node);
              } else {
                await new Promise((resolve) => setTimeout(resolve, 0));
              }
            }
            return copied;
          },
          focusByField: (field: string, value: string) => {
            const want = String(value ?? "").trim();
            const found: RowNode[] = [];
            gridApiRef.current?.forEachNodeAfterFilterAndSort?.(
              (node: RowNode) => {
                if (want && String(node.data?.[field] ?? "").trim() === want)
                  found.push(node);
              }
            );
            if (found.length === 1 && found[0].rowIndex != null) {
              selectAndFocusRow(found[0].rowIndex);
            }
            return found.length;
          },
          linkKeys: () => [...(filter?.columns ?? [])],
          fieldOf: (nameOrLabel: string) => {
            const norm = (t: unknown) =>
              String(t ?? "")
                .replace(/\s+/g, "")
                .toLowerCase();
            const want = norm(nameOrLabel);
            const columns = gridApiRef.current?.getColumns?.() || [];
            const byField = columns.find(
              (c: any) => norm(c.getColDef()?.field) === want
            );
            const byLabel = byField
              ? null
              : columns.find(
                  (c: any) => norm(c.getColDef()?.headerName) === want
                );
            return ((byField ?? byLabel)?.getColDef() as any)?.field ?? null;
          },
          codeColumnOf: (nameOrLabel: string) => {
            /* applyValues 와 같은 칸 찾기 — 행복사 전에 바꿀 코드 값을 먼저 찾는다 (2026-10-21) */
            const norm = (t: unknown) =>
              String(t ?? "")
                .replace(/\s+/g, "")
                .toLowerCase();
            const columns =
              gridApiRef.current?.getAllDisplayedColumns?.() || [];
            const column = columns.find((c: any) => {
              const d: any = c.getColDef();
              return (
                d?.field === nameOrLabel ||
                (d?.headerName && norm(d.headerName) === norm(nameOrLabel))
              );
            });
            const colDef: any = column?.getColDef();
            if (
              !colDef ||
              !isCodeColumn(colDef, focusedRowNodeRef.current?.data)
            )
              return null;
            return {
              label: colDef.headerName || colDef.field,
              popup: colDef.columnConfig.cd_popup,
              cdClas: colDef.columnConfig.cd_clas,
            };
          },
          focusIndex: (index: number) => {
            const count = gridApiRef.current?.getDisplayedRowCount?.() ?? 0;
            if (index < 0 || index >= count) return false;
            selectAndFocusRow(index);
            return true;
          },
          setQuiet: (on: boolean) => {
            chatQuietRef.current = on;
          },
          rowIds: () => {
            const ids: string[] = [];
            gridApiRef.current?.forEachNode?.((node: RowNode) => {
              if (node.id != null) ids.push(String(node.id));
            });
            return ids;
          },
          focusNew: (before: string[]) => {
            const had = new Set(before);
            let fresh: RowNode | null = null;
            gridApiRef.current?.forEachNodeAfterFilterAndSort?.(
              (node: RowNode) => {
                if (!fresh && node.id != null && !had.has(String(node.id)))
                  fresh = node;
              }
            );
            const target = fresh as RowNode | null;
            if (!target || target.rowIndex == null) return false;
            if (focusedRowNodeRef.current !== target)
              selectAndFocusRow(target.rowIndex);
            return true;
          },
          applyValues: async (values, resolveCode) => {
            const out = {
              applied: [] as string[],
              locked: [] as string[],
              notFound: [] as string[],
              unresolved: [] as string[],
            };
            const node = focusedRowNodeRef.current;
            const api = gridApiRef.current;
            if (!node || !api) return { ...out, notFound: Object.keys(values) };
            const norm = (t: unknown) =>
              String(t ?? "")
                .replace(/\s+/g, "")
                .toLowerCase();
            const columns = api.getAllDisplayedColumns?.() || [];
            for (const [key, raw] of Object.entries(values)) {
              const column = columns.find((c: any) => {
                const d: any = c.getColDef();
                return (
                  d?.field === key ||
                  (d?.headerName && norm(d.headerName) === norm(key))
                );
              });
              if (!column) {
                out.notFound.push(key);
                continue;
              }
              const colDef: any = column.getColDef();
              let editable = colDef.editable !== false;
              if (typeof colDef.editable === "function") {
                try {
                  editable = !!colDef.editable({
                    data: node.data,
                    node,
                    colDef,
                    column,
                    api,
                  });
                } catch {
                  editable = false;
                }
              }
              if (!editable) {
                out.locked.push(key);
                continue;
              }
              let value = String(raw ?? "").trim();
              const tp = getRowTpColumn(colDef, node.data);
              const cfg = colDef.columnConfig || {};
              if (
                (tp === "SnCodeBox" || tp === "SnMultiCodeBox") &&
                cfg.cd_popup
              ) {
                /* 코드 칸 — 이름으로 와도 코드박스의 이름→코드 찾기로, 딱 하나일 때만 */
                const code = await resolveCode(
                  cfg.cd_popup,
                  value,
                  cfg.cd_clas
                );
                if (!code) {
                  out.unresolved.push(key);
                  continue;
                }
                value = code;
              } else if (/^\d{4}[-./]\d{1,2}[-./]\d{1,2}$/.test(value)) {
                /* 날짜 — 그리드는 YYYYMMDD */
                const [y, m, d] = value.split(/[-./]/);
                value = `${y}${m.padStart(2, "0")}${d.padStart(2, "0")}`;
              }
              node.setDataValue(colDef.field, value);
              out.applied.push(key);
              if (isCodeColumn(colDef, node.data)) {
                await waitLinkedFill(node);
              } else {
                await new Promise((resolve) => setTimeout(resolve, 0));
              }
            }
            return out;
          },
        },
        getScreenData: () => {
          // 행추가 PK(화면 선언 lineKey.fk)는 숨김 컬럼이어도 행에 실어야 copyByKey 대조가 된다(2026-09-01).
          const addRowKeyField = onSearchStore
            .getState()
            .chatAddRowKeyField?.();
          const data = createChatScreenDataFromGrid(
            {
              getSelectedRows,
              getFilteredData,
              getAllDisplayedColumns: () =>
                gridApiRef.current?.getAllDisplayedColumns?.() || [],
              getChatFilters: () => chatFiltersRef.current,
              focusedRowNode: () => focusedRowNodeRef.current,
              forEachNode: (callback: (node: RowNode) => void) =>
                gridApiRef.current?.forEachNode?.(callback),
              forEachFilteredNode: (callback: (node: RowNode) => void) =>
                gridApiRef.current?.forEachNodeAfterFilterAndSort?.(callback),
              getDisplayedRowCount: () =>
                gridApiRef.current?.getDisplayedRowCount?.() ?? 0,
            },
            { includeFields: addRowKeyField ? [addRowKeyField] : [] }
          );
          return data;
        },
      });

    return () => {
      onSearchStore
        .getState()
        .unregisterChatScreenGridDataProvider(chatMenuCode, chatGridKey);
    };
  }, [chatMenuCode, chatGridKey]);

  // 26.09.23 kdy 포커스된 행 기준으로 하위 그리드 필터 전파 + onFocusedRowChanged 실행.
  // handleCellFocused(DOM 포커스 경로)와 selectAndFocusRow(포커스 없이 전파하는 경로)가 같은 함수를 쓴다.
  const propagateFocusedRow = (rowNode: RowNode) => {
    if (filter?.refs && filter?.columns) {
      const filterConditions: { [key: string]: any } = {};
      filter.columns.forEach((col: string) => {
        filterConditions[col] = rowNode.data?.[col];
      });
      filter.refs.forEach((fiterRef: any) => {
        fiterRef.current?.applyFilters(filterConditions);
      });
      lastFilterConditions.current = filterConditions;
    }
    if (events?.onFocusedRowChanged) {
      events.onFocusedRowChanged(rowNode.data);
    }
    lastRowNodeId.current = rowNode.id ?? null;
  };

  // 26.09.23 kdy 현재 DOM 포커스가 "다른" SnGrid 안에 있는지. 상위 그리드에서 키보드로 이동 중일 때
  // 하위 그리드가 setFocusedCell로 포커스를 뺏어가지 않도록 판별한다. 조회 버튼 등 그리드 밖 포커스는 해당 없음.
  const isFocusHeldByOtherGrid = () => {
    const active = document.activeElement as HTMLElement | null;
    if (!active || active === document.body) return false;
    const activeGridRoot = active.closest(".ag-root-wrapper");
    if (!activeGridRoot) return false;
    const myGridRoot = gridDomRef.current?.querySelector(".ag-root-wrapper");
    return activeGridRoot !== myGridRoot;
  };

  const selectAndFocusRow = (
    index: number,
    skipCellFocus: boolean = etcProps?.suppressInitialCellFocus ?? false
  ) => {
    if (!gridApiRef.current) return;

    const totalRowCount = gridApiRef.current.getDisplayedRowCount();
    if (index < 0 || index >= totalRowCount) return;

    // 1. 행 선택 (rowSelection이 'singleRow'라면 기존 선택은 자동으로 해제됨)
    const rowNode = gridApiRef.current.getDisplayedRowAtIndex(index);
    if (rowNode) {
      rowNode.setSelected(true, true); // 두 번째 인자(clearSelection)를 true로 하여 기존 선택 해제
      // 프로그램 포커스를 같은 tick에 읽히게 한다(챗봇 결합 행추가: 마스터 추가 직후 디테일이 새 마스터를
      // 읽어야 함). handleCellFocused는 setFocusedCell(rAF) 이후에야 state를 갱신하므로 여기서 ref를 선반영.
      focusedRowNodeRef.current = rowNode;
      setFocusedRowNode(rowNode);
    }

    // 2. 행이 보이도록 스크롤

    // 3. 포커스를 해당 행의 첫 번째 컬럼(또는 원하는 컬럼)으로 설정
    // 행 인덱스와 컬럼 ID가 필요합니다.
    const firstCol = gridApiRef.current.getAllDisplayedColumns()[1];

    // 26.09.23 kdy 다른 그리드(상위 그리드)가 포커스를 갖고 있으면 DOM 포커스는 건드리지 않고
    // 하위 체인 전파만 직접 수행한다. (3단/4단 체인도 각 그리드가 자기 filter.refs만 이어주면 됨)
    const focusHeldByOtherGrid = isFocusHeldByOtherGrid();
    if (focusHeldByOtherGrid) {
      // 이 그리드에 남아 있던 옛 셀 포커스/셀 범위 위치(예: 2행)는 재필터 후 다른 데이터를 가리키게 되므로 지운다.
      // 셀 범위(range)는 행 인덱스 기준이라 필터가 바뀌어도 같은 자리에 남고 채우기 핸들까지 표시되므로 함께 지운다.
      // 지우지 않으면 행 선택은 0행, 셀 테두리는 옛 행에 남아 어긋나고 getFocusedCell()도 옛 위치를 돌려준다.
      gridApiRef.current.clearRangeSelection?.();
      gridApiRef.current.clearFocusedCell();
      if (rowNode) {
        propagateFocusedRow(rowNode);
      }
    }

    if (firstCol) {
      requestAnimationFrame(() => {
        if (!gridApiRef.current) return;
        gridApiRef.current.ensureIndexVisible(index);
        // 공통팝업 등에서 SearchPanel 마지막 컨트롤 포커스를 유지해야 하는 경우
        // 행 선택/스크롤만 하고 DOM 포커스는 뺏지 않는다.
        if (!skipCellFocus && !focusHeldByOtherGrid) {
          gridApiRef.current.setFocusedCell(index, firstCol.colId);
        }
      });
    }
  };

  const selectAllFiltered = () => {
    // 1. Grid API 유효성 검사 (기존과 동일)
    if (!gridApiRef.current) {
      return;
    }
    gridApiRef.current.clearFocusedCell();
    gridApiRef.current.deselectAll(); // 기존 선택된 모든 행 초기화
    gridApiRef.current.selectAll("filtered");
  };

  const requiredValuesCheck = () => {
    try {
      // 26.09.22 kdy 필수값 체크는 화면에 보이는 컬럼만 대상으로 한다.
      // (fg_visible 이 N/H 인 숨김 컬럼, 사용자가 헤더 메뉴로 숨긴 컬럼은 제외)
      const requireds = columnConfig.filter((col) => {
        if (col.yn_required !== "Y") return false;
        const gridCol = gridApiRef.current?.getColumn(col.cd_column);
        return gridCol ? gridCol.isVisible() : col.fg_visible === "Y";
      });

      modifiedRowsRef.current.added.forEach((change) => {
        requireds.forEach((required) => {
          const key = required.cd_column; // 또는 nm_column, 실제 데이터 키에 맞게 조정
          const value = change[key];
          if (required.tp_column === "SnNumberBox") {
            if (value == null || value === 0) {
              showDialog("alter", `${required.nm_column}은(는) 필수값입니다.`);
              throw "finish";
            }
          } else {
            if (value == null || value === "") {
              showDialog("alter", `${required.nm_column}은(는) 필수값입니다.`);
              throw "finish";
            }
          }
        });
      });

      // 26.09.22 kdy 수정 목록에는 화면 코드가 setDataValue("noEvent")로 값을 밀어 넣은 행도
      // 들어오므로, 사용자가 직접 편집해 state 가 "update" 로 표시된 행만 검사한다.
      // (예전에 빈 값으로 저장돼 있던 행이 헤더 값 전파만으로 저장이 막히지 않도록)
      modifiedRowsRef.current.updated.forEach((change) => {
        if (change?.state !== "update") return;
        requireds.forEach((required) => {
          const key = required.cd_column; // 또는 nm_column, 실제 데이터 키에 맞게 조정
          const value = change[key];

          if (required.tp_column === "SnNumberBox") {
            if (value == null || value === "0") {
              showDialog("alter", `${required.nm_column}은(는) 필수값입니다.`);
              throw "finish";
            }
          } else {
            if (value == null || value === "") {
              showDialog("alter", `${required.nm_column}은(는) 필수값입니다.`);
              throw "finish";
            }
          }
        });
      });

      return true;
    } catch (Exception) {
      if (Exception !== "finish") throw Exception;
      else return false;
    }
  };

  const getMaxValue = (column: string) => {
    if (!gridApiRef.current) {
      return 0;
    }
    let maxNoLine: number = 0;
    gridApiRef.current.forEachNodeAfterFilterAndSort((node: RowNode) => {
      const value = node.data[column];
      if (value != null) {
        if (maxNoLine === null || value > maxNoLine) {
          maxNoLine = value;
        }
      }
    });
    return maxNoLine;
  };

  const getNonFilterMaxValue = (column: string) => {
    if (!gridApiRef.current) {
      return 0;
    }
    let maxValue: number = 0;
    gridApiRef.current.forEachNode((node: RowNode) => {
      const value = node.data[column];
      if (value != null) {
        if (maxValue === null || value > maxValue) {
          maxValue = value;
        }
      }
    });
    return maxValue;
  };

  const applyBlock = (node: RowNode) => {
    if (!node || !gridApiRef.current) return;
    node.setDataValue("block", true, "noEvent");
  };

  const clearBlock = (node: RowNode) => {
    if (!node || !gridApiRef.current) return;
    node.setDataValue("block", false, "noEvent");
  };

  const changeColumnConfigs = (columnSettings: ChangeColumnConfig[]) => {
    const gridApi = gridApiRef.current;
    if (!gridApi) return;

    const allColumns = gridApi.getAllGridColumns();
    if (!allColumns) return;

    // applyColumnState용 배열 준비
    const columnStateUpdates: any[] = [];

    // 1. colDef 수정 및 columnState 업데이트 준비
    allColumns.forEach((col: any) => {
      const colDef = col.getColDef();
      const field = colDef.field;
      const setting = columnSettings.find((s) => s.cd_column === field);
      if (!setting) return;

      // headerName 변경
      if (setting.nm_column !== undefined) {
        colDef.headerName = setting.nm_column;
      }

      // visible 설정
      if (setting.fg_visible !== undefined) {
        const hide = setting.fg_visible !== "Y";

        // colDef도 바꿔주고
        colDef.hide = hide;

        // 실제 컬럼 상태도 업데이트해야 함
        columnStateUpdates.push({
          colId: field,
          hide: hide,
        });
      }
    });

    // 2. 실제 컬럼 상태 적용 (이걸 하지 않으면 화면에 안 보임)
    if (columnStateUpdates.length > 0) {
      gridApi.applyColumnState({
        state: columnStateUpdates,
        applyOrder: false,
      });
    }

    // 3. 화면 갱신
    gridApi.refreshHeader();
    gridApi.refreshCells({ force: true });
  };

  const changeColumnReorderConfigs = (columnSettings: ChangeColumnConfig[]) => {
    const gridApi = gridApiRef.current;
    if (!gridApi) return;

    // applyColumnState용 배열 준비
    const columnStateUpdates: any[] = [];

    // ❗ 변경: 그리드 컬럼 기준이 아니라, "정렬된 설정값(columnSettings)" 기준으로 순회해야 합니다.
    columnSettings.forEach((setting) => {
      const col = gridApi.getColumn(setting.cd_column); // 실제 그리드에 해당 컬럼이 있는지 확인
      if (!col) return;

      const colDef = col.getColDef();
      // headerName 변경
      if (setting.nm_column !== undefined) {
        colDef.headerName = setting.nm_column;
      }

      const hide = setting.fg_visible !== "Y";
      colDef.hide = hide;

      if (setting.js_combo !== undefined) {
        const comboList = JSON.parse(setting.js_combo);
        const comboMap = new Map(
          comboList.map((item: any) => [
            item.code,
            `${item.name}[${item.code}]`,
          ])
        );
        colDef.cellEditorParams = {
          values: comboList.map((item: any) => item.code),
        };
        colDef.valueFormatter = (params: any) => {
          return comboMap.get(params.value) || params.value;
        };
      }

      // 1. 상태 업데이트 배열에 "정렬된 순서대로" push
      columnStateUpdates.push({
        colId: setting.cd_column,
        hide: hide,
      });
    });

    // 2. 실제 컬럼 상태 적용
    if (columnStateUpdates.length > 0) {
      gridApi.applyColumnState({
        state: columnStateUpdates,
        applyOrder: true, // ⭐ 반드시 true여야 배열 순서대로 화면에 그려집니다!
      });
    }
    // 3. 화면 갱신
    gridApi.refreshHeader();
    gridApi.refreshCells({ force: true });
  };

  const changeGroupColumnConfigs = (
    columnSettings: ChangeGroupColumnConfig[]
  ) => {
    const gridApi = gridApiRef.current;
    if (!gridApi) return;

    // 1. 기존 columnDefs 가져오기
    const currentDefs = gridApi.getColumnDefs();
    if (!currentDefs) return;

    // 2. group & child 컬럼 모두 설정 반영
    const updatedDefs = currentDefs.map((colDef: any) => {
      // 그룹 컬럼이면 children 처리
      if (colDef.children) {
        const updatedChildren = colDef.children.map((child: any) => {
          const setting = columnSettings.find(
            (s) => s.cd_column === child.field
          );
          if (!setting) return child;

          return {
            ...child,
            headerName:
              setting.nm_column !== undefined
                ? setting.nm_column
                : child.headerName,
            hide:
              setting.fg_visible !== undefined
                ? setting.fg_visible !== "Y"
                : child.hide,
          };
        });

        return {
          ...colDef,
          children: updatedChildren,
        };
      }

      // 일반 단일 컬럼 처리
      const setting = columnSettings.find((s) => s.cd_column === colDef.field);
      if (!setting) return colDef;

      return {
        ...colDef,
        headerName:
          setting.nm_column !== undefined
            ? setting.nm_column
            : colDef.headerName,
        hide:
          setting.fg_visible !== undefined
            ? setting.fg_visible !== "Y"
            : colDef.hide,
      };
    });

    // 3. setColumnDefs 필수 (v34는 이것만이 확실하게 반영)
    setColumnDefs(updatedDefs);
  };

  const getCheckedRows = () => {
    const checkedRows: any[] = [];
    gridApiRef.current?.forEachNode((node: RowNode) => {
      if (!node.rowPinned && node.data.fixCheckbox) {
        checkedRows.push(node.data);
      }
    });
    return checkedRows;
  };

  const getCheckedFilteredRows = () => {
    const checkedRows: any[] = [];
    gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
      if (!node.rowPinned && node.data.fixCheckbox) {
        checkedRows.push(node.data);
      }
    });
    return checkedRows;
  };

  const normalizeChatFilters = (
    filterConditions: Record<string, unknown>
  ): ChatSearchFormData =>
    Object.entries(filterConditions).reduce<ChatSearchFormData>(
      (result, [field, value]) => {
        if (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean" ||
          value === null ||
          value === undefined
        ) {
          result[field] = value;
        }
        return result;
      },
      {}
    );

  const applyFilters = (filterConditions: { [key: string]: any }) => {
    if (gridApiRef.current) {
      chatFiltersRef.current = normalizeChatFilters(filterConditions);
      const filterModel: { [key: string]: any } = {};

      gridApiRef.current.setGridOption("quickFilterText", "");

      Object.keys(filterConditions).forEach((field) => {
        const value = filterConditions[field];

        if (value === "" || value === undefined) {
          filterModel[field] = { type: "blank" };
        } else if (Array.isArray(value)) {
          // 배열인 경우: Set Filter 방식 사용
          filterModel[field] = {
            filterType: "set",
            values: value,
          };
        } else if (value === "notZero") {
          filterModel[field] = {
            filterType: "number",
            type: "notEqual",
            filter: 0,
          };
        } else if (
          typeof value === "object" &&
          value !== null &&
          Array.isArray(value.conditions)
        ) {
          // AG Grid 결합 필터 모델(operator/conditions)을 그대로 적용 (OR 조건 등)
          filterModel[field] = value;
        } else {
          // 단일 값인 경우
          filterModel[field] = {
            type: "equals",
            filter: value,
          };
        }
      });
      gridApiRef.current.setFilterModel(filterModel);
      if (gridApiRef.current.getDisplayedRowCount() < 1) {
        // 26.09.23 kdy 필터 결과가 없으면 이전 행이 focusedRowNode에 남지 않게 하고, 하위 그리드도 함께 비운다.
        clearFocusedRowState();
        propagateNoMatchToChildren();
      } else {
        selectAndFocusRow(0);
      }
      calculateTotals();
    }
  };

  // 26.09.23 kdy 상위 그리드 필터 결과가 0행일 때 호출. 어떤 행도 매칭되지 않는 퀵필터로 화면을 비운다.
  // 다음 applyFilters에서 quickFilterText가 ""로 초기화되므로 상위에 행이 생기면 자연히 해제된다.
  const applyNoMatchFilter = () => {
    if (!gridApiRef.current) return;
    gridApiRef.current.setGridOption("quickFilterText", NO_MATCH_QUICK_FILTER);
    clearFocusedRowState();
    propagateNoMatchToChildren();
    calculateTotals();
  };

  const clearFocusedRowState = () => {
    gridApiRef.current?.clearRangeSelection?.();
    gridApiRef.current?.clearFocusedCell();
    gridApiRef.current?.deselectAll();
    focusedRowNodeRef.current = undefined;
    setFocusedRowNode(undefined);
    lastRowNodeId.current = null;
    lastFilterConditions.current = {};
  };

  const propagateNoMatchToChildren = () => {
    if (filter?.refs && filter?.columns) {
      filter.refs.forEach((fiterRef: any) => {
        fiterRef.current?.applyNoMatchFilter?.();
      });
    }
  };

  const applyLikeFilters = (
    filterConditions: { [key: string]: any },
    orFields: string[] = []
  ) => {
    if (!gridApiRef.current) return;

    const isTooShort = orFields.some((field) => {
      const value = filterConditions[field];
      return value && String(value).trim().length === 1;
    });

    if (isTooShort) {
      showDialog("alter", "최소 두 글자 이상 입력해주세요.");
      return;
    }

    // 퀵필터 예외컬럼 등록
    // 1. 중복 실행 방지 (이미 설정되었다면 skip)
    if (!isapplyLikeFilter.current) {
      const api = gridApiRef.current;
      const currentDefs = api.getColumnDefs();

      if (currentDefs) {
        const newDefs = currentDefs.map((col: any) => {
          // field가 없는 컬럼은 그대로 반환
          if (!col.field) return col;

          return {
            ...col,
            // QuickFilter 동작 정의: orFields에 포함된 컬럼만 자기 값을 반환함
            getQuickFilterText: (params: any) => {
              return orFields.includes(col.field) ? (params.value ?? "") : "";
            },
          };
        });

        // 2. 새로운 컬럼 정의를 그리드에 적용
        api.setGridOption("columnDefs", newDefs);

        // 3. 플래그를 true로 설정하여 다음 호출부터는 위 로직을 안 타게 함
        isapplyLikeFilter.current = true;
      }
    }

    // --- 이후는 기존 필터 적용 로직 ---
    const filterModel: { [key: string]: any } = {};
    const quickFilterKeywords = new Set<string>();

    Object.keys(filterConditions).forEach((field) => {
      const value = filterConditions[field];
      if (value === undefined || value === "") return;

      if (orFields.includes(field) && value !== "notZero") {
        quickFilterKeywords.add(String(value).trim());
      } else if (value === "notZero") {
        filterModel[field] = {
          filterType: "number",
          type: "notEqual",
          filter: 0,
        };
      } else {
        filterModel[field] = {
          filterType: "text",
          type: "equals",
          filter: value,
        };
      }
    });

    const finalQuickFilter = Array.from(quickFilterKeywords).join(" ");

    // 퀵필터 및 필터 모델 적용
    gridApiRef.current.setGridOption("quickFilterText", finalQuickFilter);
    gridApiRef.current.setFilterModel(filterModel);

    selectAndFocusRow?.(0);
    calculateTotals?.();
  };

  const resetFilters = () => {
    if (gridApiRef.current) {
      chatFiltersRef.current = {};
      // 26.09.23 kdy 상위 0행으로 걸린 no-match 퀵필터는 리셋 시 함께 해제
      if (
        gridApiRef.current.getGridOption("quickFilterText") ===
        NO_MATCH_QUICK_FILTER
      ) {
        gridApiRef.current.setGridOption("quickFilterText", "");
      }
      gridApiRef.current.setFilterModel(null);
      gridApiRef.current.onFilterChanged();

      calculateTotals();
    }
  };

  const getFooterData = () => {
    if (gridApiRef.current) {
      // const pinnedBottomRowData = gridApiRef.current.getGridOption(
      //   "pinnedBottomRowData"
      // );
      // return pinnedBottomRowData[0];

      // 26.03.31 kdy 푸터 다시 그려주기에서 데이터 갱신으로 바꿔서 바꿈
      const footerRowNode = gridApiRef.current.getPinnedBottomRow(0);
      const rowData = (footerRowNode as any).data;
      return rowData;
    }
    return null;
  };

  const resetGrid = () => {
    gridRef.current?.api.clearCellSelection();
    gridRef.current?.api.deselectAll();
  };
  const getFlatDataFromGrid = (
    columnKeys: string[]
  ): Map<string, FlatRowType> => {
    const flatRowMap = new Map<string, FlatRowType>();

    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      const raw = node.data;
      const flat: FlatRowType = {};

      columnKeys.forEach((key) => {
        const rawVal = raw[key];

        let val = raw[key];
        let displayValue = rawVal;

        if (typeof val === "object" && val !== null) {
          val = val.label ?? val.text ?? val.value ?? JSON.stringify(val);
        }

        if (isDateValue(val)) {
          flat[key] = {
            value:
              val.length === 8
                ? `${val.slice(0, 4)}/${val.slice(4, 6)}/${val.slice(6, 8)}`
                : val,
            dataType: "dateTime" as const,
          };
        }
        //  앞에 0이 붙은 코드 → 먼저 처리
        else if (
          typeof val === "string" &&
          /^\d+$/.test(val) &&
          val.startsWith("0")
        ) {
          flat[key] = {
            value: `'${val}`, // 또는 '=' + '"' + val + '"'
            dataType: "string",
          };
        } else if (isNumeric(val)) {
          flat[key] = {
            value: Number(val),
            dataType: "number" as const,
          };
        } else {
          //  앞자리 0 보존을 위한 처리

          flat[key] = {
            value: val ?? "",
            dataType: "string" as const,
          };
        }
      });

      flatRowMap.set(node.id!, flat);
    });

    return flatRowMap;
  };

  const setCheckedAllWithFilter = (check: boolean) => {
    gridApiRef.current?.forEachNodeAfterFilterAndSort((node: RowNode) => {
      node.setDataValue("fixCheckbox", check, "noEvent");
    });
  };

  const setCheckedAll = (check: boolean) => {
    gridApiRef.current?.forEachNode((node: RowNode) => {
      node.setDataValue("fixCheckbox", check, "noEvent");
    });
  };

  const sharedCellValueChanged = (change: any) => {
    if (!gridApiRef.current) return;
    const api = gridApiRef.current;

    let targetRowNode: RowNode | null = null;
    api.forEachNode((node: any) => {
      if (node.data.KeyField === change.KeyField) {
        targetRowNode = node;
      }
    });
    if (!targetRowNode) return;

    const updatedData = {
      // ...targetRowNode.data,
      ...change,
    };

    api.applyTransaction({ update: [updatedData] });

    const addedIdx = modifiedRowsRef.current.added.findIndex(
      (row) => row.KeyField === change.KeyField
    );
    const updatedIdx = modifiedRowsRef.current.updated.findIndex(
      (row) => row.KeyField === change.KeyField
    );

    if (addedIdx !== -1) {
      modifiedRowsRef.current.added[addedIdx] = updatedData;
    } else if (updatedIdx !== -1) {
      modifiedRowsRef.current.updated[updatedIdx] = updatedData;
    } else {
      modifiedRowsRef.current.updated.push(updatedData);
    }

    // 2. 합계 재계산
    calculateTotals();
  };

  const setValueImmediately = (rowNode: RowNode, colId: string, value: any) => {
    if (!gridApiRef.current) return;
    const newData = { ...rowNode.data, [colId]: value };
    gridApiRef.current.applyTransaction({ update: [newData] });

    const index = modifiedRowsRef.current.added.findIndex(
      (row) => row.KeyField === newData.KeyField
    );

    if (rowNode?.rowPinned) return;
    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRowsRef.current.added[index] = newData;
    } else {
      const index = modifiedRowsRef.current.updated.findIndex(
        (row) => row.KeyField === newData.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRowsRef.current.updated[index] = newData;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRowsRef.current.updated.push(newData);
      }
    }
    if (filter?.refs && filter?.columns) {
      filter.refs.map((fiterRef: any) => {
        const filterConditions: { [key: string]: any } = {};
        filter?.columns?.forEach((col: string) => {
          if (col === colId) {
            filterConditions[col] = value;
          } else {
            filterConditions[col] = newData[col];
          }
        });
        fiterRef.current.applyFilters(filterConditions);
      });
    }
    calculateTotals();
  };
  //#endregion

  //#region 그리드 이벤트
  const handleCellClicked = async (params: any) => {
    if (events?.onCellClicked) {
      const param = {
        colId: params.column.colId,
        node: params.node,
      };
      events.onCellClicked(param);
    }
  };

  const handleCellFocused = async (event: CellFocusedEvent) => {
    const currentRowNode = gridApiRef.current.getDisplayedRowAtIndex(
      event.rowIndex
    );

    if (events?.onCellFocused) {
      events.onCellFocused();
    }

    if (!currentRowNode) {
      lastRowNodeId.current = null;
      lastFilterConditions.current = {};
      return;
    }

    setFocusedRowNode(currentRowNode);
    if (
      currentRowNode != focusedRowNode ||
      currentRowNode.id !== lastRowNodeId.current
    ) {
      propagateFocusedRow(currentRowNode);
    } else {
      // 26.04.29 kdy 다중그리드 채번 후 필터 깨지는거 때문에 추가
      if (filter?.refs && filter?.columns) {
        filter.refs.map((fiterRef: any) => {
          const filterConditions: { [key: string]: any } = {};
          filter?.columns?.forEach((col: string) => {
            filterConditions[col] = currentRowNode.data[col];
          });
          if (
            JSON.stringify(lastFilterConditions.current) !==
            JSON.stringify(filterConditions)
          ) {
            fiterRef.current.applyFilters(filterConditions);
          }
        });
      }
    }
  };

  // 26.08.21 행 드래그로 순서 변경.
  // rowDragManaged는 필터/정렬이 걸리면 AG Grid가 이동을 막으므로(shouldPreventRowMove)
  // 순번만 다시 매기고 그 컬럼 오름차순으로 정렬해서 자리를 맞춘다.
  const handleInternalRowDragEnd = (event: any) => {
    const orderKey = etcProps?.rowDragOrderKey;
    const { api, node: movedNode, overNode } = event;
    if (!orderKey || !movedNode || !overNode || movedNode === overNode) return;

    const nodes: RowNode[] = [];
    api.forEachNodeAfterFilterAndSort((node: RowNode) => {
      if (node.data) nodes.push(node);
    });
    const from = nodes.indexOf(movedNode);
    const to = nodes.indexOf(overNode);
    if (from < 0 || to < 0) return;
    nodes.splice(to, 0, ...nodes.splice(from, 1));

    // 신규(insert) 행은 백엔드가 키를 매기므로 재번호 대상에서 제외한다.
    const targets = nodes.filter((n) => n.data?.state !== "insert");
    // 기존 orderKey 값이 전부 고유하면 그 값 집합(슬롯)을 보존한다 —
    // orderKey 가 PK 이거나 여러 그리드가 값 공간을 공유할 때(예: 주간일지 실적/계획은
    // no_line 을 공유) 1..N 재번호는 PK 충돌을 일으켜 데이터가 섞인다. 그 외엔 1..N.
    const nums = targets.map((n) => Number(n.data[orderKey]));
    const keepSlots =
      nums.every((v) => !isNaN(v)) && new Set(nums).size === nums.length;
    const slots = keepSlots
      ? [...nums].sort((a, b) => a - b)
      : targets.map((_, i) => i + 1);
    // setDataValue로 넣어야 변경이력(getChange) 등록까지 공통 로직을 탄다.
    targets.forEach((node: RowNode, index: number) =>
      node.setDataValue(orderKey, slots[index], "noEvent")
    );
    api.applyColumnState({
      state: [{ colId: orderKey, sort: "asc" }],
      defaultState: { sort: null },
    });
    api.refreshClientSideRowModel("group");

    events?.onRowDragEnd && events.onRowDragEnd(event);
  };

  const handleCodeTyped = async (event: any, result: any, byKey: boolean) => {
    const api = gridApiRef.current;
    if (!api) return;
    const colId = event.column?.getColId?.() ?? event.colDef?.field;
    const focused = api.getFocusedCell();
    // 조회하는 동안 행이 빠졌거나 포커스가 다른 칸·그리드 밖(저장 버튼 등)으로 갔으면 손대지 않는다
    const stillHere =
      api.getRowNode(event.node.id) === event.node &&
      focused?.rowIndex === event.node.rowIndex &&
      focused?.column?.getColId() === colId &&
      !!gridDomRef.current?.contains(document.activeElement);

    if (!result?.needPopup) {
      if (byKey && stillHere) moveToNextCell(event.node.data, colId);
      return;
    }
    const picked =
      byKey && stillHere && !chatQuietRef.current
        ? await openPopup({
            api: event.api,
            colDef: event.colDef,
            node: event.node,
          })
        : false;
    // 관련 컬럼은 건드리지 않았으므로 코드 칸만 되돌리면 된다. 도움창 값 표시로 넣어 재조회를 막는다.
    if (!picked && api.getRowNode(event.node.id) === event.node)
      setPopupValue(event.node, colId, event.oldValue ?? "");
  };

  const handleInternalCellValueChanged = async (event: any) => {
    if (event.node?.rowPinned) return;
    if (!gridApiRef.current) return;

    const codeResult = event.colDef?.codeValueChanged?.(event);
    const pending = pendingCodeTypedRef.current;
    const byKey =
      event.source === "edit" &&
      !!pending &&
      pending.node === event.node &&
      pending.colId === (event.column?.getColId?.() ?? event.colDef?.field);
    if (byKey) pendingCodeTypedRef.current = null;
    // 행별 타입으로 조회를 건너뛴 칸(codeResult 없음)도 Enter/Tab 이면 이동은 해야 한다
    if (codeResult || byKey) {
      Promise.resolve(codeResult).then((result) =>
        handleCodeTyped(event, result, byKey)
      );
    }

    const colId = event.colDef.field;
    if (colId === "fixCheckbox") {
      if (event.source !== "noEvent") {
        selectAndFocusRow(event.node.rowIndex);
        if (events?.onCheckedRowChaged) {
          events.onCheckedRowChaged(event.newValue, "row");
        }
      }
      return;
    }
    gridApiRef.current.applyTransaction({ update: [event.data] });

    if (
      events?.onCellValueChanged &&
      event.source !== "noEvent" &&
      event.source !== "noChange"
    ) {
      const data = {
        rowData: event.data,
        newValue: event.newValue,
        oldValue: event.oldValue,
        rowIndex: event.rowIndex,
        colId: colId,
        rowNode: event.node,
      };
      events.onCellValueChanged(data);
    }

    if (event.source !== "noChange") {
      // 화면 onCellValueChanged 안에서 setValueImmediately(applyTransaction)로 행 데이터를 새 객체로 바꿨을 수 있다.
      // event.data는 그 이전 객체라 여기서 그대로 넣으면 핸들러가 세팅한 값이 변경이력(getChange)에서 빠진다.
      const rowData = event.node?.data ?? event.data;
      // 26.09.22 kdy 사용자 편집(입력·도움창·붙여넣기)으로 바뀐 기존 행은 state 를 "update" 로 표시한다.
      // 화면 코드의 setDataValue(..., "noEvent") 전파는 표시하지 않는다. resetUpdate 에서 지워진다.
      if (event.source !== "noEvent" && rowData.state !== "insert") {
        rowData.state = "update";
      }
      const index = modifiedRowsRef.current.added.findIndex(
        (row) => row.KeyField === rowData.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRowsRef.current.added[index] = rowData;
      } else {
        const index = modifiedRowsRef.current.updated.findIndex(
          (row) => row.KeyField === rowData.KeyField
        );

        if (index !== -1) {
          // KeyField가 동일한 row를 Change로 변경
          modifiedRowsRef.current.updated[index] = rowData;
        } else {
          // KeyField가 없는 경우 추가하는 로직 (선택 사항)
          modifiedRowsRef.current.updated.push(rowData);
        }
      }
      if (filter?.refs && filter?.columns) {
        if (filter?.columns.includes(colId)) {
          filter.refs.map((fiterRef: any) => {
            const filterConditions: { [key: string]: any } = {};
            filter?.columns?.forEach((col: string) => {
              if (col === event.colDef.field) {
                filterConditions[col] = event.newValue;
              } else {
                filterConditions[col] = event.data[col];
              }
            });
            fiterRef.current.applyFilters(filterConditions);
          });
        }
      }
    }

    calculateTotals();
    if (events?.onAfterSetFooter) {
      events.onAfterSetFooter(gridApiRef.current.getPinnedBottomRow(0)?.data);
    }
  };

  const handleGridReady = (params: any) => {
    const { api, columnApi } = params;
    gridApiRef.current = params.api;
    tryResolveColumnsReady();
  };

  const handleOnRowDoubleClicked = (event: any) => {
    if (events?.onRowDoubleClicked) {
      events?.onRowDoubleClicked(event);
    }
  };

  const handleCellDoubleClicked = async (params: any) => {
    const value = params.value;
    const data = params.data;
    if (params.colDef.columnConfig?.yn_drilldown === "Y") {
      if (!value) return;
      const params = {
        id: "CommonMapper",
        queryId: "getDrillDown",
        params: {
          cd_firm: userInfo.cd_firm,
          no_docu: value,
        },
      };
      const result = await fnGetDataSet(params);
      if (result.length > 0) {
        const menuList = useMenuStore.getState().menuItems;
        const matchedItem = menuList.find(
          (item: any) => item.cd_menu === result[0][0].cd_menu
        );
        const cdModule = matchedItem ? matchedItem.cd_module : null;

        const filePath =
          `./${cdModule}/${result[0][0].cd_menu}/${result[0][0].cd_menu}`.toUpperCase();
        const drillDown = menuList
          ? menuList.find((item: any) => item?.cd_menu === result[0][0].cd_menu)
          : undefined;

        if (drillDown) {
          (window as any).onActivate(
            drillDown.cd_menu, // 메뉴 키
            drillDown.nm_menu, // 메뉴 제목
            drillDown.cd_module, // 모듈
            filePath, // 파일 경로
            drillDown, // 데이터
            drillDown.nm_path, // 파일경로 한글화
            data,
            "fg_drilldown.Search"
          );
        }
      }
    } else if (
      getRowTpColumn(params.colDef, data) === "SnCodeBox" ||
      getRowTpColumn(params.colDef, data) === "SnMultiCodeBox"
    ) {
      openPopup(params);
    } else {
      if (events?.onCellDoubleClicked) {
        events?.onCellDoubleClicked(params);
      }
    }
  };

  const handleKeyboardNavigation = (
    params: SuppressKeyboardEventParams
  ): boolean => {
    const { event, api, column, node } = params;

    if (!event) {
      return false;
    }

    const key = event.key;
    const isEditing = api.getEditingCells().length > 0;

    // Ctrl+C 는 ag-grid 기본 셀 범위 복사에 맡긴다(선택된 행 전체를 복사하지 않는다).
    // 클릭한 셀 한 칸만 복사되고, 드래그로 범위를 잡은 경우에는 그 범위만 복사된다.
    switch (key) {
      case "Tab":
      case "Enter": {
        // 코드 칸에 값을 바꿔 쳤으면 여기서 이동하지 않는다. 조회가 끝난 뒤 이동하거나 도움창을 띄운다(handleCodeTyped).
        // 먼저 이동해 버리면 조회 중에 다음 칸에 치던 값이 도움창 선택 때 지워진다.
        if (isEditing && isCodeColumn(column.getColDef(), node.data)) {
          const input = event.target as HTMLInputElement | null;
          const editor: any = api.getCellEditorInstances({
            rowNodes: [node],
            columns: [column],
          })[0];
          const typed =
            input?.tagName === "INPUT" ? input.value : editor?.getValue?.();
          const before = node.data?.[column.getColId()];
          if (
            typed !== undefined &&
            String(typed ?? "").trim() !== String(before ?? "").trim()
          ) {
            pendingCodeTypedRef.current = { node, colId: column.getColId() };
            api.stopEditing(false);
            return true;
          }
        }
        api.stopEditing(false);
        moveToNextCell(node.data, column.getColId());
        return true;
      }

      case " ":
      case "Spacebar":
        if (etcProps?.ischeckboxSelection && api.getSelectedRows().length > 1) {
          api.forEachNodeAfterFilterAndSort((rowNode) => {
            if (rowNode.isSelected()) {
              const currentValue = rowNode.data.fixCheckbox;
              if (filter?.refs && filter?.columns) {
                filter.refs.map((fiterRef: any) => {
                  const filterConditions: { [key: string]: any } = {};
                  filter?.columns?.forEach((col: string) => {
                    filterConditions[col] = rowNode.data[col];
                  });
                  fiterRef.current.applyFilters(filterConditions);
                });
              }
              rowNode.setDataValue("fixCheckbox", !currentValue, "noEvent");
              if (events?.onCheckedRowChaged) {
                events.onCheckedRowChaged(!currentValue, "row");
              }
            }
          });
          event.preventDefault();
          return true;
        } else if (!isEditing && column.getColDef().editable) {
          api.startEditingCell({
            rowIndex: node.rowIndex!,
            colKey: column.getColId(),
            key: event.key,
          });
          event.preventDefault(); // 기본 Space 동작(셀 선택 토글) 차단
          return true; // AG Grid 기본 Space 동작 차단
        }
        break;
      case "ArrowUp":
      case "ArrowDown":
        if (isEditing) {
          params.event.preventDefault();
        } else {
          requestAnimationFrame(() => {
            const focused = params.api.getFocusedCell();
            if (focused) {
              const rowNode = params.api.getDisplayedRowAtIndex(
                focused.rowIndex
              );
              params.api.deselectAll();
              rowNode?.setSelected(true);
            }
          });
        }
        return false;
      default:
        return false;
    }

    return false;
  };

  const handleContextMenu = (params: any) => {
    // 단순 우클릭 이벤트(메뉴 선택 전) 무시
    if (!params?.cd_contextmenu) return;

    if (params.cd_contextmenu === "common") {
      const cd_function = params.cd_function;
      if (!cd_function) return;

      const hasChanges = (getChange().modifiedRowcount ?? 0) > 0;
      if (hasChanges) {
        showDialog("alter", "수정중인 내용이 있습니다. 저장 후 처리하세요.");
        return;
      }

      const rowNodes: any[] = [focusedRowNodeRef.current ?? focusedRowNode];
      if (!rowNodes || rowNodes.length === 0) return;

      switch (cd_function) {
        case "fn_sn_fin_com_autocount_01": // 회계처리
          FN_Fi_AutoSlip("Y", params.cd_menu, rowNodes);
          break;
        case "fn_sn_fin_com_autocount_del_01": // 회계취소
          FN_Fi_AutoSlip("N", params.cd_menu, rowNodes);
          break;
      }
    } else if (events?.onContextMenu) {
      events?.onContextMenu({
        id: params.cd_contextmenu,
        menu: params.cd_menu_target,
        functionId: params.cd_function, // DB cd_function 전달
      });
    }
  };

  const onCellEditingStopped = (event: any) => {
    if (event.colDef?.field === "fixCheckbox") return;
    const tpColumn = getRowTpColumn(event.colDef, event.node?.data);
    if (tpColumn == "SnDateBox" || tpColumn == "SnComboBox") {
      const focusedCell = event.api.getFocusedCell();
      if (
        focusedCell &&
        focusedCell.column.getColId() === event.colDef?.field
      ) {
        moveToNextCell(event.node.data, event.colDef?.field);
      }
    }
  };

  const onSortChanged = (event: any) => {
    releaseInitialSort(event);
    if (events?.onSortChanged) {
      events?.onSortChanged();
    }
    gridApiRef.current.refreshCells({
      columns: ["fixIndicator"],
      force: true,
    });
  };

  const onFilterChanged = () => {
    if (events?.onFilterChanged) {
      events?.onFilterChanged();
    }
    // 26.09.22 kdy No. 컬럼은 rowIndex 기반 valueGetter 라 필터가 바뀌어도 셀이 다시 그려지지 않아
    // 이전 순번이 그대로 남는다(부모 그리드 연동 필터 포함). 정렬과 같이 강제로 다시 그린다.
    gridApiRef.current?.refreshCells({
      columns: ["fixIndicator"],
      force: true,
    });
  };

  const onCellRangeChanged = (params: any) => {
    // 드래그 아닐 때 무시 (클릭 등)
    if (!params.started && !params.finished) return;

    const ranges = params.api.getCellRanges();
    if (!ranges || ranges.length === 0) return;

    ranges.forEach((range: any) => {
      const hasCheckbox = range.columns.some(
        (col: any) => col.getColId() === "fixCheckbox"
      );

      if (!hasCheckbox) return;

      const start = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
      const end = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

      for (let i = start; i <= end; i++) {
        const node = params.api.getDisplayedRowAtIndex(i);
        if (!node || node.rowPinned) continue;

        // 👉 그냥 true로 통일 (토글 ❌)
        node.setDataValue("fixCheckbox", true);
      }
    });
  };

  const handleCellSelectionChanged = (params: any) => {
    const focusedCell = params.api.getFocusedCell();

    if (!focusedCell || focusedCell.column.getColId() !== "fixCheckbox") return;
    const node = params.api.getDisplayedRowAtIndex(focusedCell.rowIndex);

    if (!node) return;
    const ranges = params.api.getCellRanges();

    if (params.finished) {
      if (ranges.length > 0) {
        if (ranges[0].endRow.rowIndex === focusedCell.rowIndex) {
          fixCheckbox.current = !node.data.fixCheckbox;
        }
      }
    }

    const checked = node.data.fixCheckbox;

    ranges?.forEach((range: any) => {
      const start = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
      const end = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

      params.api.forEachNodeAfterFilterAndSort((node: any) => {
        if (node.rowPinned) return;

        if (node.rowIndex >= start && node.rowIndex <= end) {
          if (filter?.refs && filter?.columns) {
            filter.refs.map((fiterRef: any) => {
              const filterConditions: { [key: string]: any } = {};
              filter?.columns?.forEach((col: string) => {
                filterConditions[col] = node.data[col];
              });
              fiterRef.current.applyFilters(filterConditions);
            });
          }
          node.setDataValue("fixCheckbox", fixCheckbox.current, "noEvent");
          if (node.rowIndex === end) {
            requestAnimationFrame(() => {
              params.api.ensureIndexVisible(node.rowIndex);

              node.setSelected(true, true);
            });
          }

          if (events?.onCheckedRowChaged) {
            events.onCheckedRowChaged(fixCheckbox.current, "row");
          }
        }
      });
    });
  };

  const handleRowDataUpdated = () => {
    if (events?.onRowDataUpdated) {
      events.onRowDataUpdated();
    }

    if (!isMemoizedRowDataChanged) return;

    if (memoizedRowData && memoizedRowData?.length < 1) {
      if (gridApiRef.current.getDisplayedRowCount() < 1) {
        setFocusedRowNode(undefined);
      }
      return;
    }
    if (!gridApiRef.current) return;
    // 초기 조회에서는 모델 갱신이 모두 끝난 시점(onModelUpdated)에 0행 포커스를 주기 위해 대기한다.
    isInitialFocusPending.current = true;

    setIsMemoizedRowDataChanged(false);
  };

  // 26.08.21 kdy 맨 앞 컬럼 기본 오름차순 정렬.
  // rowData가 그리드에 들어가기 전에 정렬 상태를 확정해 두면 모델 갱신이 한 번으로 끝난다.
  const applyInitialSort = () => {
    const api = gridApiRef.current;
    if (!api?.getColumnState) return;
    if (etcProps?.enableInitialSort === false) return;

    const hasActiveSort = api
      .getColumnState()
      .some((colState: any) => !!colState.sort);
    if (hasActiveSort) return;

    const firstColumn = api.getAllDisplayedColumns()?.find((col: any) => {
      const id = col.getColId();
      return id !== "fixIndicator" && id !== "fixCheckbox";
    });
    if (!firstColumn?.colDef.sortable) return;

    // 26.10.30 기본 정렬은 헤더에 화살표를 표시하지 않는다 (releaseInitialSort에서 해제)
    initialSortColIdRef.current = firstColumn.getColId();
    gridDomRef.current?.classList.add("sn-initial-sort");
    api.applyColumnState({
      state: [{ colId: firstColumn.getColId(), sort: "asc" }],
      defaultState: { sort: null },
    });
  };

  // 26.10.30 사용자가 직접 정렬하거나(헤더 클릭/메뉴) 기본 정렬이 다른 정렬로 바뀌면(행 드래그 등)
  // 화살표 숨김을 해제한다. Shift+클릭 멀티정렬이면 숨은 기본 정렬이 1순위로 남아
  // 사용자 정렬이 먹지 않으므로 기본 정렬을 뺀다.
  const releaseInitialSort = (event: any) => {
    const initialColId = initialSortColIdRef.current;
    const api = gridApiRef.current;
    if (!initialColId || !api?.getColumnState) return;

    const sorted = api
      .getColumnState()
      .filter((colState: any) => !!colState.sort);
    const initialStillAsc = sorted.some(
      (colState: any) =>
        colState.colId === initialColId && colState.sort === "asc"
    );
    const isUserSort = event?.source === "uiColumnSorted";
    if (!isUserSort && initialStillAsc && sorted.length === 1) return;

    initialSortColIdRef.current = null;
    gridDomRef.current?.classList.remove("sn-initial-sort");

    if (initialStillAsc && sorted.length > 1) {
      api.applyColumnState({ state: [{ colId: initialColId, sort: null }] });
    }
  };

  // 26.08.21 kdy 조회 직후 0행 포커스/선택 + 스크롤 최상단 고정.
  const runInitialFocus = () => {
    const api = gridApiRef.current;
    if (!api || !isInitialFocusPending.current) return;

    const rowCount = api.getDisplayedRowCount?.() || 0;
    if (rowCount < 1) return; // 아직 행이 없으면 대기 상태를 유지한다.

    isInitialFocusPending.current = false;

    // 포커스/선택을 먼저 맞춘 뒤, 다음 페인트에서 스크롤을 0행 상단으로 재보정한다.
    api.clearRangeSelection();
    api.clearFocusedCell();
    api.deselectAll();
    selectAndFocusRow(0);
    calculateTotals();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // 조회의 기본 동작은 "0행 상단 고정"
        gridApiRef.current?.ensureIndexVisible(0, "top");

        // 26.08.21 kdy 이전 포커스 복원처럼 0행이 아닌 위치가 필요한 화면은 onRowDataReady에서 호출
        if (events?.onRowDataReady) {
          events.onRowDataReady();
        }
      });
    });
  };

  const resolveComboExportName = (rawValue: any, colDef: any) => {
    if (rawValue === null || rawValue === undefined) return "";
    const columnType = colDef?.columnConfig?.tp_column;
    if (columnType !== "SnComboBox") return rawValue;

    const colId = colDef?.colId ?? colDef?.field;
    const comboCol = columnConfig.find((c) => c.cd_column === colId);
    const dataSource = Array.isArray(comboCol?.js_combo)
      ? comboCol?.js_combo
      : typeof comboCol?.js_combo === "string" &&
          comboCol?.js_combo.trim() !== ""
        ? JSON.parse(comboCol?.js_combo)
        : [];

    const matched = dataSource.find(
      (item: any) => String(item?.code) === String(rawValue)
    );
    return matched?.name ?? rawValue;
  };

  // 26.10.26 kdy Excel export 시 SnCodeBox는 코드 대신 화면에 보이는 명칭(cd_displaycolumn)으로 내보낸다.
  // 명칭은 복사(processCellForClipboard)와 같은 getFindText로 구하고, 비어 있으면 기존대로 코드값을 내보낸다.
  const resolveCodeBoxExportName = (rawValue: any, colDef: any, data?: any) => {
    if (getRowTpColumn(colDef, data) !== "SnCodeBox") return rawValue;
    if (typeof colDef?.getFindText !== "function") return rawValue;
    const text = colDef.getFindText({ colDef, data, value: rawValue });
    const displayText = converter.getString(text);
    return displayText === "" ? rawValue : displayText;
  };

  // 26.09.23 kdy Excel export 시 날짜 컬럼(YYYYMMDD/YYYYMM) 값을 Excel 날짜 셀로 변환
  // SnGridExcelStyles(DateTime)와 컬럼 cellClass가 매칭되며 값은 yyyy-mm-ddThh:mm:ss 형식이어야 함
  // 형식이 맞지 않는 값은 빈 셀로 내보내 Excel 파일 손상을 방지
  const resolveDateExportValue = (rawValue: any, colDef: any, data?: any) => {
    const columnType = getRowTpColumn(colDef, data);
    const digits = String(rawValue ?? "").replace(/[^0-9]/g, "");

    // 날짜 셀로 내보낼 컬럼 타입 목록
    switch (columnType) {
      case "SnDateBox":
        return digits.length === 8
          ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}T00:00:00`
          : "";
      case "SnDateMonthBox":
        return digits.length === 6
          ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-01T00:00:00`
          : "";
      default:
        return rawValue;
    }
  };

  const buildExcelExportParams = (baseParams: any = {}) => {
    const userProcess = baseParams?.processCellCallback;

    // ischeckboxSelection으로 추가된 선택용 체크박스 컬럼은 내보내기 대상에서 제외
    const columnKeys =
      baseParams?.columnKeys ??
      (etcProps?.ischeckboxSelection
        ? gridApiRef.current
            ?.getAllDisplayedColumns()
            ?.map((col: any) => col.getColId())
            ?.filter((colId: string) => colId !== "fixCheckbox")
        : undefined);

    return {
      // 하단 합계(서머리) 행은 내보내기에서 제외
      skipPinnedBottom: true,
      ...baseParams,
      ...(columnKeys ? { columnKeys } : {}),
      processCellCallback: (p: any) => {
        const colDef = p?.column?.getColDef?.() ?? p?.colDef;

        // Excel export 시 SnComboBox는 내부 code 대신 표시 name으로 변환
        const comboConverted = resolveComboExportName(p?.value, colDef);

        // Excel export 시 SnCodeBox는 코드 대신 명칭(거래처명 등)으로 변환
        const codeBoxConverted = resolveCodeBoxExportName(
          comboConverted,
          colDef,
          p?.node?.data
        );

        // SnDateBox/SnDateMonthBox 값은 Excel 날짜 셀(yyyy-mm-ddThh:mm:ss)로 내보내기
        const converted = resolveDateExportValue(
          codeBoxConverted,
          colDef,
          p?.node?.data
        );

        // SnCheckBox -> Y/N 변환
        const checkboxConverted =
          colDef?.columnConfig?.tp_column === "SnCheckBox"
            ? converted
              ? "Y"
              : "N"
            : converted;

        // 문자열 숫자값이 0으로 시작하면 텍스트로 강제해 선행 0을 보존
        const normalized =
          typeof checkboxConverted === "string" &&
          /^0\d+$/.test(checkboxConverted.trim())
            ? `'${checkboxConverted.trim()}`
            : checkboxConverted;

        return typeof userProcess === "function"
          ? userProcess({ ...p, value: normalized })
          : normalized;
      },
    };
  };

  const getContextMenuItems = (params: any) => {
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");
    const formattedTime = today.toTimeString().slice(0, 8).replace(/:/g, "");

    const defaultItems = Array.isArray(params.defaultItems)
      ? params.defaultItems
      : [];
    if (defaultItems.length === 0) {
      return [];
    }
    if (defaultItems.length > 6) {
      defaultItems[6] = {
        name: "내보내기",
        subMenu: [
          {
            name: "CSV로 내보내기",
            action: () =>
              gridRef.current!.api.exportDataAsExcel(
                buildExcelExportParams({
                  fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}_${formattedDate}${formattedTime}.csv`,
                  sheetName: "다운로드",
                })
              ),
          },
          {
            name: "Excel로 내보내기",
            action: () =>
              gridRef.current!.api.exportDataAsExcel(
                buildExcelExportParams({
                  fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}_${formattedDate}${formattedTime}.xlsx`,
                  sheetName: "다운로드",
                })
              ),
          },
        ],
      };
    }
    const customMenuItems: any[] = [];
    let oldgroup: any = null;

    menuItems.forEach((item: any) => {
      if (oldgroup !== item.group) {
        customMenuItems.push("separator");
        oldgroup = item.group;
      }

      customMenuItems.push({
        name: item.name,
        action: item.action,
      });
    });
    return [...defaultItems, ...customMenuItems];
  };

  // 복사(Ctrl+C / 컨텍스트메뉴 복사) 시 SnComboBox·SnCodeBox 컬럼은 코드 대신 화면에 보이는 텍스트를 넣는다.
  // 표시 텍스트는 찾기(Ctrl+F)와 같은 getFindText로 구하고, 비어 있으면 기존대로 코드값을 복사한다.
  const processCellForClipboard = (params: any) => {
    const colDef: any = params.column?.getColDef?.() ?? {};
    const tpColumn = getRowTpColumn(colDef, params.node?.data);
    if (tpColumn !== "SnComboBox" && tpColumn !== "SnCodeBox") {
      return params.value;
    }
    if (typeof colDef.getFindText !== "function") return params.value;
    const text = colDef.getFindText({
      ...params,
      colDef,
      data: params.node?.data,
    });
    const displayText = converter.getString(text);
    return displayText === "" ? params.value : displayText;
  };

  const processDataFromClipboard = (params: any) => {
    if (enablePasteOnlyAdd) {
      const { data, api } = params;
      if (!data || data.length === 0) return null;
      if (api.getDisplayedRowCount() < 1) return null;

      const focusedCell = api.getFocusedCell();
      if (!focusedCell) return null;

      const focusedRowNode = api.getDisplayedRowAtIndex(focusedCell.rowIndex);

      // 현재 포커스된 행이 insert 상태인지 체크
      if (!focusedRowNode || focusedRowNode.data?.state !== "insert") {
        return null;
      }

      const allDisplayedColumns = api.getAllDisplayedColumns();
      const startRowIndex = focusedCell.rowIndex;
      const colId = focusedCell.column.getColId(); // 현재 포커스된 컬럼 ID
      let startColIndex = allDisplayedColumns.findIndex(
        (col: any) => col.getColId() === focusedCell.column.getColId()
      );

      // 2. 반복문 실행
      for (let i = 0; i < data.length; i++) {
        const rowValues = data[i];

        // 빈 값 체크
        const isAllEmpty = rowValues.every(
          (val: any) => val === null || String(val).trim() === ""
        );
        if (isAllEmpty) continue;

        let currentRowIndex = startRowIndex + i;
        let targetNode = api.getDisplayedRowAtIndex(currentRowIndex);

        // 현재 셀에 값이 있는지 확인
        const currentValue = targetNode?.data?.[colId];
        const hasValue =
          currentValue !== null &&
          currentValue !== undefined &&
          String(currentValue).trim() !== "";

        // 1. 데이터 준비
        let finalRowData: Record<string, any>;

        if (hasValue || !targetNode) {
          // 새 행을 위한 기본값 생성
          finalRowData = (
            props.events?.onBeforeAddRow ? props.events.onBeforeAddRow() : {}
          ) as Record<string, any>;
        } else {
          // 기존 행 데이터 가져오기
          finalRowData = { ...targetNode.data };
        }

        // 2. 클립보드 데이터 주입
        rowValues.forEach((value: any, colOffset: number) => {
          const targetCol = allDisplayedColumns[startColIndex + colOffset];
          if (targetCol) {
            finalRowData[targetCol.getColId()] = value;
          }
        });

        // 3. 행 추가 또는 업데이트 실행
        if (hasValue || !targetNode) {
          // 💡 새 행 추가 시 데이터를 인자로 전달!
          addRow(finalRowData, false);
        } else {
          // 기존 행은 트랜잭션으로 업데이트
          const newKey = targetNode.data.KeyField || generateKey();

          const updatedData = {
            ...finalRowData,
          };

          // 2. 내부 데이터 추적용 Ref(added 배열) 동기화
          // 이미 added에 들어있을 기존 객체를 찾아 값을 덮어씌웁니다.
          const existingAddedRow = modifiedRowsRef.current.added.find(
            (item: any) => item.KeyField === newKey
          );

          if (existingAddedRow) {
            Object.keys(finalRowData).forEach((key) => {
              existingAddedRow[key] = finalRowData[key];
            });
          }

          if (targetNode && targetNode.data) {
            Object.keys(finalRowData).forEach((key) => {
              targetNode.data[key] = finalRowData[key];
            });

            // 2. 주소가 유지된 targetNode.data를 그대로 트랜잭션에 전달
            api.applyTransaction({ update: [targetNode.data] });
          }
        }
      }
      return null;
    }
    return null;
  };

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter", // 헤더 필터 비활성화 (타입을 명시해야 정상 작동됨.)
      floatingFilter: false, // 플로팅 필터 비활성화
      resizable: true,
      suppressMenu: true,
      enableRowGroup: true,
      headerClass: "header-center",
      suppressFlash: true,
      suppressKeyboardEvent: handleKeyboardNavigation,
      // etcProps.wrapText 를 켠 그리드만: 셀 값의 \n 을 줄바꿈으로 표시하고 행 높이를 내용에 맞춘다.
      // (wrapText 만 켜면 white-space:normal 이라 \n 이 공백으로 눌리므로 pre-line 을 같이 준다.)
      ...(etcProps?.wrapText
        ? {
            wrapText: true,
            autoHeight: true,
            cellStyle: { whiteSpace: "pre-line" as const },
          }
        : {}),
    }),
    [etcProps?.wrapText]
  );

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 200,
      suppressMenu: false,
      cellRendererParams: {
        innerRenderer: (params: any) => {
          return params.value;
        },
      },
      mainMenuItems: (
        params: GetMainMenuItemsParams
      ): (MenuItemDef | DefaultMenuItem)[] => {
        const customMenuItems: MenuItemDef[] = [
          {
            name: "모든 그룹 해제",
            action: () => {
              params.api.setRowGroupColumns([]); // 모든 그룹 해제
            },
          },
          {
            name: "전체 확장",
            action: () => {
              params.api.expandAll(); // 전체 확장
            },
          },
          {
            name: "전체 축소",
            action: () => {
              params.api.collapseAll(); // 전체 축소
            },
          },
        ];

        // ✅ 필요하다면 기본 메뉴 항목도 추가 가능
        // return ['pinSubMenu', 'separator', ...customMenuItems];
        return customMenuItems;
      },
    };
  }, []);

  const find = useSnGridFind(gridApiRef); // Ctrl+F 찾기

  //#region 그리드 렌더링
  const renderGridContent = () => {
    return (
      <div
        className="SnGrid"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
        {...find.wrapperProps}
      >
        {/* 찾기 바 (Ctrl+F) */}
        <SnGridFindBar {...find.barProps} />
        {/* 그리드 */}
        <div
          ref={gridDomRef}
          className="SnGrid ag-theme-alpine"
          style={{ flex: "1 1 auto" }}
        >
          <AgGridReact
            rowStyle={{ fontSize: StyleSettings.font }}
            containerStyle={{ fontSize: StyleSettings.font }}
            ref={gridRef}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            rowData={keyRowData}
            pinnedTopRowData={props.pinnedTopRowData}
            autoGroupColumnDef={autoGroupColumnDef}
            pinnedBottomRowData={initPinnedBottomRowData}
            suppressFocusAfterRefresh={true}
            suppressCellFocus={false}
            suppressHeaderFocus={true} // 26.09.23 kdy 방향키로 헤더까지 포커스가 올라가지 않게(헤더는 마우스로만 조작)
            rowSelection={rowSelection}
            animateRows={true}
            cellSelection={{
              handle: {
                mode: "fill",
                setFillValue: (params: any) => {
                  if (!checkEditable(params.rowNode.data, params.column.colDef))
                    return;
                  const newValue = params.values.at(-1);
                  return newValue;
                },
              },
            }}
            suppressNoRowsOverlay={true}
            suppressMenuHide={false}
            localeText={localeText}
            rowGroupPanelShow={
              etcProps?.rowGroupPanelShow || "onlyWhenGrouping"
            }
            domLayout="normal"
            popupParent={
              popupParent ||
              etcProps?.popupParent ||
              popupContainer ||
              document.querySelector("body")
            }
            suppressHorizontalScroll={false}
            suppressDragLeaveHidesColumns={
              !!etcProps?.suppressDragLeaveHidesColumns
            }
            suppressRowVirtualisation={false}
            suppressColumnVirtualisation={
              !!etcProps?.suppressColumnVirtualisation
            }
            groupDefaultExpanded={-1}
            headerHeight={30}
            rowHeight={30}
            //loadingOverlayComponent={"agLoadingOverlay"}
            onGridReady={handleGridReady}
            onCellClicked={handleCellClicked}
            onCellFocused={handleCellFocused}
            onCellValueChanged={handleInternalCellValueChanged}
            onCellDoubleClicked={handleCellDoubleClicked}
            onCellContextMenu={handleContextMenu}
            getContextMenuItems={getContextMenuItems}
            excelStyles={SnGridExcelStyles} // 26.09.23 kdy 날짜 컬럼 Excel 날짜 셀 내보내기
            onRowDoubleClicked={handleOnRowDoubleClicked}
            onCellSelectionChanged={handleCellSelectionChanged}
            onRowDataUpdated={handleRowDataUpdated}
            onCellEditingStopped={onCellEditingStopped}
            onRowDragEnd={handleInternalRowDragEnd}
            onModelUpdated={() => {
              if (isDeleteFocusPending.current.pending) {
                const { index: deletedRowIndex, pending } =
                  isDeleteFocusPending.current;
                handleRowFocusAfterDelete(deletedRowIndex);
                isDeleteFocusPending.current = { pending: false, index: 0 };
              }
              if (isInitialFocusPending.current) {
                // 26.08.21 kdy 한 번의 조회에서 modelUpdated가 여러 번 발생할 수 있으므로
                // (정렬/필터 재적용, 행 높이 확정 등) 보정을 예약해두고 마지막 갱신에서만 실행한다.
                if (initialFocusRaf.current !== null) {
                  cancelAnimationFrame(initialFocusRaf.current);
                }
                initialFocusRaf.current = requestAnimationFrame(() => {
                  initialFocusRaf.current = null;
                  runInitialFocus();
                });
              }
              events?.onModelUpdated && events?.onModelUpdated();
            }}
            getRowId={(params) => params.data.KeyField}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            processCellForClipboard={processCellForClipboard}
            processDataFromClipboard={
              enablePasteOnlyAdd ? processDataFromClipboard : undefined
            }
            getRowStyle={etcProps?.getRowStyle}
            context={{
              onCheckedRowChaged: events?.onCheckedRowChaged,
            }}
          />
        </div>
      </div>
    );
  };
  //#endregion

  //#region return
  return (
    <>
      {etcProps?.title ? (
        <SnTab
          titles={[etcProps?.title]}
          width={"100%"}
          onTitleDoubleClick={(_, e) => {
            if (e.ctrlKey && e.shiftKey) openDataInspector();
          }}
          header={[
            <SnTabHeader
              onBtnAddClick={events?.onBtnAddClick}
              onBtnDeleteClick={events?.onBtnDeleteClick}
              body={header}
              headerInfo={headerInfo}
              onHeaderValueChanged={events?.onHeaderValueChanged}
              onBtnEtcClick={events?.onBtnEtcClick}
              onHeaderEnter={events?.onHeaderEnter}
            />,
          ]}
          body={[renderGridContent()]}
        />
      ) : (
        renderGridContent()
      )}
    </>
  );
  //#endregion
});
export default React.memo(SnGrid);
